import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../App';
import collegeConfig from '../../config/collegeConfig';
import { generateAnalyticsReport } from '../../utils/analyticsReportGenerator';
import { generateStudentActivityReport, generateFacultyActivityReport } from '../../utils/monitorReportGenerator';
import { generateLogbookPDF } from '../StudentDashboard/pdfGenerator';
import { generateClassicDRPCertificate } from '../../utils/classicDrpCertificateGenerator';
import { saveFile } from '../../utils/saveFile';
import {
  FaSignOutAlt, FaUsers, FaChalkboardTeacher, FaBookMedical,
  FaCheckCircle, FaChartBar, FaSync, FaUserGraduate, FaHospital,
  FaUniversity, FaStar, FaDownload,
  FaCertificate, FaClipboardList,
  FaTh, FaFileAlt, FaTimes, FaChevronRight,
  FaClock, FaChartPie, FaGraduationCap, FaCalendarWeek,
  FaUserFriends, FaClipboardCheck, FaHistory, FaSpinner
} from 'react-icons/fa';
import {
  MAROON, COLORS, rateColor, CustomTooltip,
  Card, StatCard, Badge, ProgressBar, SectionHeader, SearchInput,
  Spinner, Modal
} from './monitorUI';
import { StudentDetailModal, TeacherDetailModal, UGStudentDetailModal, UGTeacherDetailModal } from './detailModals';
import StudentDocumentsModal from '../shared/StudentDocumentsModal';
import EligibilityCards from '../shared/EligibilityCards';

const TABS = [
  { id: 'overview',    label: 'Overview',             icon: <FaTh /> },
  { id: 'departments', label: 'Departments',          icon: <FaUniversity /> },
  { id: 'students',    label: 'Students',             icon: <FaUserGraduate /> },
  { id: 'teachers',    label: 'Teachers',             icon: <FaChalkboardTeacher /> },
  { id: 'team',        label: 'Faculty ↔ Student Map',icon: <FaUserFriends /> },
  { id: 'eligibility', label: 'Exam Eligibility',     icon: <FaClipboardCheck /> },
  { id: 'drp',         label: 'DRP',                  icon: <FaHospital /> },
  { id: 'academic',    label: 'Academic Monitoring',  icon: <FaGraduationCap /> },
  { id: 'grades',      label: 'Student Grades',       icon: <FaClipboardList /> },
  { id: 'reports',     label: 'Reports',              icon: <FaFileAlt /> },
];

// ─── Reports Tab ─────────────────────────────────────────────────────────────
function ReportsTab({ api, token, departments, program }) {
  const [period, setPeriod] = useState('monthly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [generating, setGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [ugBatches, setUgBatches] = useState([]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];

  // UG: load batches once for the filter dropdown
  useEffect(() => {
    if (program !== 'UG') return;
    fetch(`${api}/ug/batches`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setUgBatches(d.batches); }).catch(() => {});
  }, [program, api, token]);

  // Reset course/batch selection when switching program
  useEffect(() => { setSelectedCourse(''); }, [program]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    const params = new URLSearchParams({ period, year: selectedYear, month: selectedMonth });
    if (selectedCourse) params.set(program === 'UG' ? 'batchId' : 'courseId', selectedCourse);
    try {
      const url = program === 'UG' ? `${api}/monitor/ug-report-data?${params}` : `${api}/monitor/report-data?${params}`;
      const r = await fetch(url, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setPreviewData(d);
    } catch (_) {}
    setPreviewLoading(false);
  }, [api, token, period, selectedYear, selectedMonth, selectedCourse, program]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleDownload = async () => {
    if (!previewData) return;
    setGenerating(true);
    try {
      if (program === 'UG') {
        const scopeLabel = selectedCourse
          ? `Batch: ${ugBatches.find(b => b._id === selectedCourse)?.batchLabel || 'Selected'}`
          : 'All UG Batches';
        await generateAnalyticsReport({
          reportType: period,
          periodLabel: previewData.period.label,
          scopeLabel,
          summary: {
            ...previewData.summary,
            totalCourses: previewData.subjects?.length,
            drpTotal: 0, drpVerified: 0, drpCertified: 0
          },
          teacherActivity: previewData.teacherActivity,
          studentActivity: previewData.studentActivity,
          courseBreakdown: null,
          collegeName: collegeConfig.collegeName,
          logoSrc: collegeConfig.logo,
        });
      } else {
        const scopeLabel = selectedCourse
          ? `Course: ${departments.find(d => d.courseId?.toString() === selectedCourse)?.courseName || 'Selected'}`
          : 'All Departments';
        await generateAnalyticsReport({
          reportType: period,
          periodLabel: previewData.period.label,
          scopeLabel,
          summary: { ...previewData.summary, totalCourses: previewData.courses?.length },
          teacherActivity: previewData.teacherActivity,
          studentActivity: previewData.studentActivity,
          courseBreakdown: previewData.courses?.length > 1 ? departments : null,
          collegeName: collegeConfig.collegeName,
          logoSrc: collegeConfig.logo,
        });
      }
    } catch (e) {
      alert('PDF generation failed: ' + e.message);
    }
    setGenerating(false);
  };

  const s = previewData?.summary;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* Controls */}
      <Card style={{ padding:'16px 20px' }}>
        <SectionHeader icon={<FaFileAlt />} title="Generate Analytics Report" color={MAROON} />
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
          {['daily','weekly','monthly','annual'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
              background: period === p ? MAROON : '#f0f0f0',
              color: period === p ? '#fff' : '#555',
              transition:'all 0.15s'
            }}>
              {p.charAt(0).toUpperCase()+p.slice(1)}
            </button>
          ))}
        </div>

        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          {(period === 'monthly' || period === 'annual') && (
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              style={{ padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none' }}
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          {period === 'monthly' && (
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(parseInt(e.target.value))}
              style={{ padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none' }}
            >
              {months.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          )}
          <select
            value={selectedCourse}
            onChange={e => setSelectedCourse(e.target.value)}
            style={{ padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', flex:1, minWidth:160 }}
          >
            {program === 'UG' ? (
              <>
                <option value=''>All UG Batches</option>
                {ugBatches.map(b => <option key={b._id} value={b._id}>{b.batchLabel}</option>)}
              </>
            ) : (
              <>
                <option value=''>All Departments</option>
                {departments.map(d => (
                  <option key={d.courseId} value={d.courseId}>{d.courseName}</option>
                ))}
              </>
            )}
          </select>
          <button
            onClick={handleDownload}
            disabled={generating || !previewData}
            style={{ padding:'9px 20px', background: generating ? '#aaa' : MAROON, color:'#fff', border:'none', borderRadius:8, cursor: generating ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:8 }}
          >
            <FaDownload style={{ animation: generating ? 'pulse 1s infinite' : 'none' }} />
            {generating ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </Card>

      {/* Preview stats */}
      {previewLoading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>
      ) : s && (
        <>
          <div style={{ padding:'2px 4px', background:MAROON+'15', borderRadius:10, border:`1px solid ${MAROON}30` }}>
            <p style={{ margin:0, padding:'8px 14px', fontSize:13, color:MAROON, fontWeight:700 }}>
              Preview: {previewData.period.label}
            </p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px,1fr))', gap:12 }}>
            <StatCard icon={<FaUserGraduate />} label="Total Students" value={s.totalStudents} color="#4A90E2" sub={`${s.activeStudents} active`} />
            <StatCard icon={<FaChalkboardTeacher />} label="Total Teachers" value={s.totalTeachers} color="#9B59B6" />
            <StatCard icon={<FaBookMedical />} label={program === 'UG' ? 'Competency Logs' : 'Logbook Entries'} value={s.totalEntries} color="#E67E22" sub={`${s.pending} pending`} />
            <StatCard icon={<FaCheckCircle />} label="Approval Rate" value={`${s.approvalRate}%`} color={rateColor(s.approvalRate)} sub={`${s.approved} approved`} />
            {program === 'UG' ? (
              <StatCard icon={<FaClipboardList />} label="Mini-Module Entries" value={Object.values(s.miniModuleActivity || {}).reduce((a,b)=>a+b,0)} color={MAROON} />
            ) : (
              <>
                <StatCard icon={<FaHospital />} label="DRP Students" value={s.drpTotal} color="#16a085" />
                <StatCard icon={<FaCertificate />} label="Certificates" value={s.drpCertified} color={MAROON} sub={`${s.drpVerified} verified`} />
              </>
            )}
          </div>

          {/* Faculty activity */}
          {previewData.teacherActivity?.length > 0 && (
            <Card>
              <SectionHeader icon={<FaChalkboardTeacher />} title="Faculty Activity Preview" color="#9B59B6" />
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>
                      {['Faculty Name','Department','Role','Verified','Pending','Status'].map(h => (
                        <th key={h} style={{ background:'#f8f9fa', padding:'8px 10px', textAlign: (h==='Faculty Name'||h==='Department')?'left':'center', fontWeight:700, color:'#555', borderBottom:'2px solid #e9ecef', fontSize:11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.teacherActivity.map((t,i) => {
                      const status = t.reviewed === 0 ? 'Inactive' : t.reviewed >= 5 ? 'Active' : 'Low';
                      const sc = status === 'Active' ? '#27AE60' : status === 'Inactive' ? '#E74C3C' : '#F39C12';
                      return (
                        <tr key={i} style={{ borderBottom:'1px solid #f0f0f0' }}>
                          <td style={{ padding:'8px 10px', fontWeight:600 }}>{t.name}{t.designation ? ` (${t.designation})` : ''}</td>
                          <td style={{ padding:'8px 10px', fontSize:11, color:'#555' }}>{t.department || '—'}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center' }}>{t.isHOD ? <Badge label="HOD" color='#fff' bg={MAROON} /> : 'Guide'}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center', color:'#27AE60', fontWeight:700 }}>{t.reviewed}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center', color: t.pending>0?'#F39C12':'#aaa', fontWeight: t.pending>0?700:400 }}>{t.pending}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center' }}><Badge label={status} color='#fff' bg={sc} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Top students */}
          {previewData.studentActivity?.filter(s => s.submitted > 0).length > 0 && (
            <Card>
              <SectionHeader icon={<FaUserGraduate />} title="Top Active Students" color="#27AE60" />
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>
                      {['#','Name','Reg. No.','Course','Submitted','Approved','Rate'].map(h => (
                        <th key={h} style={{ background:'#f8f9fa', padding:'8px 10px', textAlign: h==='Name'||h==='Course'?'left':'center', fontWeight:700, color:'#555', borderBottom:'2px solid #e9ecef', fontSize:11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.studentActivity.filter(s => s.submitted > 0).slice(0, 20).map((st, i) => (
                      <tr key={i} style={{ borderBottom:'1px solid #f0f0f0' }}>
                        <td style={{ padding:'8px 10px', textAlign:'center', color: i<3?'#F39C12':'#aaa', fontWeight: i<3?700:400 }}>{i+1}</td>
                        <td style={{ padding:'8px 10px', fontWeight:600 }}>{st.name}</td>
                        <td style={{ padding:'8px 10px', textAlign:'center', color:'#888', fontSize:11 }}>{st.registrationNumber||'—'}</td>
                        <td style={{ padding:'8px 10px', fontSize:11 }}>{st.course||'—'}</td>
                        <td style={{ padding:'8px 10px', textAlign:'center', color:'#4A90E2', fontWeight:700 }}>{st.submitted}</td>
                        <td style={{ padding:'8px 10px', textAlign:'center', color:'#27AE60', fontWeight:700 }}>{st.approved}</td>
                        <td style={{ padding:'8px 10px', textAlign:'center' }}><Badge label={`${st.rate}%`} color='#fff' bg={rateColor(st.rate)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Academic Monitoring Tab ───────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Faculty who have left still show up in most monitor lists (only the team
// roster hides them outright) — this appends their leaving date next to the name.
const leftDateLabel = (leavingDate) => leavingDate
  ? ` (left ${new Date(leavingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })})`
  : '';

// Most faculty names start with "Dr." — taking the first word for chart labels
// used to just show "Dr." for nearly every bar. Strip the title instead.
const chartFacultyLabel = (name) => {
  const stripped = (name || '').replace(/^(dr|prof|mr|mrs|ms)\.?\s+/i, '');
  return stripped.length > 16 ? `${stripped.slice(0, 15)}…` : stripped;
};

function weekLabel(wk, yr, mo) {
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const d1 = (wk - 1) * 7 + 1;
  const d2 = Math.min(wk * 7, daysInMonth);
  return `Week ${wk}  (${d1} – ${d2} ${MONTHS[mo - 1].slice(0, 3)})`;
}

function weeksInMonth(yr, mo) {
  const days = new Date(yr, mo, 0).getDate();
  return Math.ceil(days / 7);
}

function currentWeek() {
  return Math.ceil(new Date().getDate() / 7);
}

function AcademicMonitoringTab({ api, token, departments, program }) {
  const now = new Date();
  const [courseId,  setCourseId]  = useState('');
  const [batch,     setBatch]     = useState('');
  const [year,      setYear]      = useState(now.getFullYear());
  const [month,     setMonth]     = useState(now.getMonth() + 1);
  const [week,      setWeek]      = useState(currentWeek());
  const [batches,   setBatches]   = useState([]);
  const [batchLoad, setBatchLoad] = useState(false);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [expanded,  setExpanded]  = useState({});
  const [ugSubjects, setUgSubjects] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };
  const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i);
  const weekOptions = Array.from({ length: weeksInMonth(year, month) }, (_, i) => i + 1);

  // When week goes out of range after month change, clamp it
  useEffect(() => {
    const max = weeksInMonth(year, month);
    if (week > max) setWeek(max);
  }, [year, month, week]);

  // Reset selection when switching program
  useEffect(() => { setCourseId(''); setBatch(''); setData(null); }, [program]);

  // UG: load subject list once
  useEffect(() => {
    if (program !== 'UG') return;
    fetch(`${api}/ug/subjects`, { headers }).then(r => r.json()).then(d => { if (d.success) setUgSubjects(d.subjects); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, api, token]);

  // Load batches when course/subject changes
  useEffect(() => {
    if (!courseId) { setBatches([]); setBatch(''); return; }
    setBatchLoad(true);
    if (program === 'UG') {
      fetch(`${api}/ug/batches`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setBatches(d.batches.map(b => ({ value: b._id, label: b.batchLabel }))); })
        .catch(() => {})
        .finally(() => setBatchLoad(false));
    } else {
      fetch(`${api}/monitor/academic-batches?courseId=${courseId}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setBatches(d.batches.map(b => ({ value: b, label: b }))); })
        .catch(() => {})
        .finally(() => setBatchLoad(false));
    }
    setBatch('');
    setData(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, program]);

  const loadData = async () => {
    if (!courseId || !batch) return;
    setLoading(true);
    setData(null);
    try {
      const params = program === 'UG'
        ? new URLSearchParams({ subjectId: courseId, batchId: batch, year, month, week })
        : new URLSearchParams({ courseId, batch, year, month, week });
      const url = program === 'UG' ? `${api}/monitor/ug-academic-monitoring?${params}` : `${api}/monitor/academic-monitoring?${params}`;
      const r = await fetch(url, { headers });
      const d = await r.json();
      if (d.success) {
        const normalized = program === 'UG'
          ? { ...d, modules: [{ moduleId: 'ug-subject', moduleName: d.subjectName, topics: d.topics, conducted: d.topics.length, totalEntries: d.totalEntriesFound }], courseName: d.subjectName }
          : d;
        setData(normalized);
        const exp = {};
        (normalized.modules || []).forEach(m => { exp[m.moduleId] = true; });
        setExpanded(exp);
      }
    } catch (_) {}
    setLoading(false);
  };

  const toggleModule = id => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const sel = { padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:0 };
  const batchLabel = batches.find(b => b.value === batch)?.label || batch;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* ── Selectors card ── */}
      <Card>
        <SectionHeader icon={<FaGraduationCap />} title="Academic Monitoring" color={MAROON} />
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

          {/* Row 1: Department/Subject + Batch */}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <div style={{ flex:'1 1 200px', display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>{program === 'UG' ? 'Subject' : 'Department'}</label>
              <select value={courseId} onChange={e => setCourseId(e.target.value)} style={sel}>
                <option value=''>{program === 'UG' ? '— Select Subject —' : '— Select Department —'}</option>
                {program === 'UG'
                  ? ugSubjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)
                  : departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
              </select>
            </div>
            <div style={{ flex:'1 1 160px', display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Batch</label>
              <select value={batch} onChange={e => setBatch(e.target.value)} style={sel} disabled={!courseId || batchLoad}>
                <option value=''>{batchLoad ? 'Loading…' : courseId ? '— Select Batch —' : '— Select above first —'}</option>
                {batches.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Year + Month + Week */}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div style={{ flex:'1 1 110px', display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Year</label>
              <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div style={{ flex:'1 1 130px', display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Month</label>
              <select value={month} onChange={e => setMonth(Number(e.target.value))} style={sel}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex:'2 1 200px', display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:700, color:'#555', display:'flex', alignItems:'center', gap:5 }}><FaCalendarWeek style={{ color:MAROON }} /> Week</label>
              <select value={week} onChange={e => setWeek(Number(e.target.value))} style={sel}>
                {weekOptions.map(w => <option key={w} value={w}>{weekLabel(w, year, month)}</option>)}
              </select>
            </div>
            <button
              onClick={loadData}
              disabled={!courseId || !batch || loading}
              style={{ padding:'9px 22px', background:MAROON, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor: (!courseId || !batch || loading) ? 'not-allowed' : 'pointer', opacity: (!courseId || !batch || loading) ? 0.6 : 1, flexShrink:0 }}
            >
              {loading ? 'Loading…' : 'Load Report'}
            </button>
          </div>
        </div>
      </Card>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ display:'flex', justifyContent:'center', padding:50 }}><Spinner /></div>
      )}

      {/* ── Results ── */}
      {data && !loading && (
        <>
          {/* Period banner */}
          <div style={{ background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 16px', fontSize:13, color:'#444', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FaCalendarWeek style={{ color:MAROON }} />
              <strong>{data.courseName}</strong> &nbsp;·&nbsp; Batch: <strong>{batchLabel}</strong>
              &nbsp;·&nbsp; {weekLabel(data.period.week, data.period.year, data.period.month)}, {MONTHS[data.period.month - 1]} {data.period.year}
            </div>
            <div style={{ display:'flex', gap:16, fontSize:12, color:'#777' }}>
              <span><strong style={{ color:'#4A90E2' }}>{data.totalStudentsInBatch}</strong> students in batch</span>
              <span><strong style={{ color:MAROON }}>{data.totalEntriesFound}</strong> entries this week</span>
            </div>
          </div>

          {data.totalEntriesFound === 0 && (
            <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:10, padding:'14px 18px', fontSize:13, color:'#92400e', display:'flex', alignItems:'center', gap:10 }}>
              <FaClock style={{ flexShrink:0 }} />
              <span>No entries were submitted in this week. Try selecting a different week or month where students were active.</span>
            </div>
          )}

          {data.modules.length === 0 && data.totalEntriesFound > 0 && (
            <Card><p style={{ textAlign:'center', color:'#999', padding:30 }}>No modules found for this selection.</p></Card>
          )}

          {data.modules.filter(mod => mod.totalEntries > 0).map(mod => (
            <Card key={mod.moduleId} style={{ padding:0, overflow:'hidden' }}>
              {/* Module header */}
              <div
                onClick={() => toggleModule(mod.moduleId)}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 18px', background:MAROON+'0d', borderBottom:'1px solid '+MAROON+'22', cursor:'pointer', gap:10 }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <FaBookMedical style={{ color:MAROON, fontSize:15 }} />
                  <span style={{ fontWeight:800, fontSize:15, color:'#1a1a2e' }}>{mod.moduleName}</span>
                  <span style={{ fontSize:12, color:'#888', background:'#fff', padding:'2px 10px', borderRadius:20, border:'1px solid #e5e7eb' }}>
                    {mod.topics.length} topic{mod.topics.length !== 1 ? 's' : ''} · {mod.totalEntries} entr{mod.totalEntries !== 1 ? 'ies' : 'y'}
                  </span>
                </div>
                <FaChevronRight style={{ color:MAROON, fontSize:13, transform: expanded[mod.moduleId] ? 'rotate(90deg)' : 'none', transition:'transform 0.2s' }} />
              </div>

              {expanded[mod.moduleId] && (
                <div style={{ padding:'0 0 0 0' }}>
                  {mod.topics.length === 0 ? (
                    <p style={{ textAlign:'center', color:'#bbb', padding:24, fontSize:13 }}>No entries this week.</p>
                  ) : (
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                        <thead>
                          <tr>
                            {['#', 'Topic / Title', 'Students', 'Faculty', 'Status'].map(h => (
                              <th key={h} style={{ background:'#f8f9fa', padding:'10px 14px', textAlign:'left', fontWeight:700, color:'#555', borderBottom:'2px solid #e9ecef', fontSize:12, whiteSpace:'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {mod.topics.map((t, i) => (
                            <tr key={i} style={{ borderBottom:'1px solid #f0f0f0' }}>
                              <td style={{ padding:'10px 14px', color:'#aaa', fontWeight:600, fontSize:12, width:32 }}>{i + 1}</td>
                              <td style={{ padding:'10px 14px', fontWeight:600, color:'#1a1a2e', maxWidth:240 }}>{t.topic}</td>
                              <td style={{ padding:'10px 14px', fontSize:12, color:'#444', maxWidth:280 }}>
                                {t.students.length === 0
                                  ? <span style={{ color:'#bbb' }}>—</span>
                                  : t.students.join(', ')
                                }
                              </td>
                              <td style={{ padding:'10px 14px', fontSize:12, color:'#555', maxWidth:200 }}>
                                {t.faculty.length === 0
                                  ? <span style={{ color:'#bbb' }}>Not verified yet</span>
                                  : t.faculty.join(', ')
                                }
                              </td>
                              <td style={{ padding:'10px 14px', whiteSpace:'nowrap' }}>
                                {t.pending > 0 && (
                                  <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#FEF3C730', border:'1px solid #F39C1240', borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:700, color:'#d97706', marginRight:5 }}>
                                    <FaClock style={{ fontSize:9 }} /> {t.pending} pending
                                  </span>
                                )}
                                {t.verified > 0 && (
                                  <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#D1FAE530', border:'1px solid #27AE6040', borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:700, color:'#27AE60' }}>
                                    <FaCheckCircle style={{ fontSize:9 }} /> {t.verified} verified
                                  </span>
                                )}
                                {t.pending === 0 && t.verified === 0 && (
                                  <span style={{ color:'#bbb', fontSize:12 }}>—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {/* Conducted footer */}
                        <tfoot>
                          <tr>
                            <td colSpan={5} style={{ padding:'12px 16px', background:'#f8f9fa', borderTop:'2px solid #e9ecef', textAlign:'right' }}>
                              <span style={{ fontSize:13, color:'#555' }}>
                                Total <strong style={{ color:MAROON }}>{mod.moduleName}</strong> Conducted this week:{' '}
                                <span style={{ fontSize:18, fontWeight:900, color:MAROON }}>{mod.conducted}</span>
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </>
      )}

    </div>
  );
}

// ─── Exam Eligibility Tab ──────────────────────────────────────────────────────
function ExamEligibilityTab({ api, token, departments }) {
  const [courseId, setCourseId] = useState('');
  const [batch, setBatch] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchLoad, setBatchLoad] = useState(false);
  const [eligibilityList, setEligibilityList] = useState([]);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [docsStudent, setDocsStudent] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const sel = { padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:0 };

  // Load batches when department changes
  useEffect(() => {
    if (!courseId) { setBatches([]); setBatch(''); return; }
    setBatchLoad(true);
    fetch(`${api}/monitor/academic-batches?courseId=${courseId}`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setBatches(d.batches); })
      .catch(() => {})
      .finally(() => setBatchLoad(false));
    setBatch('');
    setFetched(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const checkEligibility = async () => {
    if (!courseId) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ courseId, ...(batch ? { batch } : {}) });
      const r = await fetch(`${api}/exam-eligibility/batch?${params}`, { headers });
      const d = await r.json();
      if (d.success) { setEligibilityList(d.eligibilityList); setFetched(true); }
      else setError(d.message || 'Failed to check eligibility');
    } catch (_) {
      setError('Failed to check eligibility');
    }
    setLoading(false);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <Card>
        <SectionHeader icon={<FaClipboardCheck />} title="Exam Eligibility Check" color={MAROON} />
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          <div style={{ flex:'1 1 200px', display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Department</label>
            <select value={courseId} onChange={e => setCourseId(e.target.value)} style={sel}>
              <option value=''>— Select Department —</option>
              {departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
            </select>
          </div>
          <div style={{ flex:'1 1 160px', display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Batch</label>
            <select value={batch} onChange={e => setBatch(e.target.value)} style={sel} disabled={!courseId || batchLoad}>
              <option value=''>{batchLoad ? 'Loading…' : 'All Batches'}</option>
              {batches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <button
            onClick={checkEligibility}
            disabled={!courseId || loading}
            style={{ padding:'9px 22px', background:MAROON, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor: (!courseId || loading) ? 'not-allowed' : 'pointer', opacity: (!courseId || loading) ? 0.6 : 1, flexShrink:0 }}
          >
            {loading ? 'Checking…' : 'Check Eligibility'}
          </button>
        </div>
      </Card>

      {error && (
        <Card style={{ borderLeft:'4px solid #E74C3C' }}>
          <p style={{ color:'#E74C3C', margin:0, fontSize:13 }}>{error}</p>
        </Card>
      )}

      {loading && (
        <div style={{ display:'flex', justifyContent:'center', padding:50 }}><Spinner /></div>
      )}

      {fetched && !loading && (
        <EligibilityCards eligibilityList={eligibilityList} onViewDocuments={setDocsStudent} />
      )}

      {!fetched && !loading && !error && (
        <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>Select a department (and optionally a batch) and click <strong>Check Eligibility</strong>.</p></Card>
      )}

      {docsStudent && (
        <StudentDocumentsModal studentId={docsStudent.id} courseId={courseId} onClose={() => setDocsStudent(null)} canUploadCertifications />
      )}
    </div>
  );
}

// ─── Student Grades Tab ────────────────────────────────────────────────────────
const teacherName = (u) => u ? `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.trim() || 'Unknown' : 'Unknown';

function GradesTab({ api, token, departments, program }) {
  const [courseId, setCourseId] = useState('');
  const [batch, setBatch] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchLoad, setBatchLoad] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [gradedStudentIds, setGradedStudentIds] = useState(new Set());
  const [studentSearch, setStudentSearch] = useState('');
  const [gradesStudent, setGradesStudent] = useState(null);
  const [checklistEvals, setChecklistEvals] = useState([]);
  const [assessmentSubs, setAssessmentSubs] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);

  // UG mini-module matrix
  const [ugModules, setUgModules] = useState([]);
  const [ugRows, setUgRows] = useState([]);
  const [ugLoading, setUgLoading] = useState(false);
  const [ugBatchFilter, setUgBatchFilter] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const sel = { padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:0 };

  // UG: load batches once, then the matrix whenever the batch filter changes
  useEffect(() => {
    if (program !== 'UG') return;
    fetch(`${api}/ug/batches`, { headers }).then(r => r.json()).then(d => { if (d.success) setBatches(d.batches); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, api, token]);

  useEffect(() => {
    if (program !== 'UG') return;
    setUgLoading(true);
    const params = new URLSearchParams();
    if (ugBatchFilter) params.set('batchId', ugBatchFilter);
    fetch(`${api}/monitor/ug-grades?${params}`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) { setUgModules(d.modules); setUgRows(d.students); } })
      .catch(() => {})
      .finally(() => setUgLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, ugBatchFilter, api, token]);

  // Load batches when department changes (PG)
  useEffect(() => {
    if (program !== 'PG') return;
    if (!courseId) { setBatches([]); setBatch(''); setStudents([]); return; }
    setBatchLoad(true);
    fetch(`${api}/monitor/academic-batches?courseId=${courseId}`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setBatches(d.batches); })
      .catch(() => {})
      .finally(() => setBatchLoad(false));
    setBatch('');
    setStudents([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, program]);

  // Load the batch's students once both department and batch are picked
  useEffect(() => {
    if (!courseId || !batch) { setStudents([]); setGradedStudentIds(new Set()); return; }
    setStudentsLoading(true);
    fetch(`${api}/monitor/all-students?courseId=${courseId}`, { headers })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          const inBatch = (d.students || []).filter(s =>
            (s.courses || []).some(c => c.courseId?.toString() === courseId && c.batch === batch)
          );
          setStudents(inBatch);
        }
      })
      .catch(() => {})
      .finally(() => setStudentsLoading(false));

    fetch(`${api}/monitor/batch-grade-status?courseId=${courseId}&batch=${encodeURIComponent(batch)}`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setGradedStudentIds(new Set(d.gradedStudentIds)); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, batch]);

  const openStudentGrades = async (student) => {
    setGradesStudent(student);
    setGradesLoading(true);
    setChecklistEvals([]);
    setAssessmentSubs([]);
    try {
      const [ceRes, asRes] = await Promise.all([
        fetch(`${api}/checklist-evaluations/student/${student._id}/evaluations`, { headers }).then(r => r.json()),
        fetch(`${api}/assessments/student/${student._id}/submissions`, { headers }).then(r => r.json())
      ]);
      if (ceRes.success) setChecklistEvals(ceRes.evaluations || []);
      if (asRes.success) setAssessmentSubs(asRes.submissions || []);
    } catch (_) {}
    setGradesLoading(false);
  };

  const filteredStudents = students.filter(s => {
    if (!studentSearch) return true;
    const q = studentSearch.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.registrationNumber || '').toLowerCase().includes(q);
  });

  const isGraded = (it) => (it.grade !== null && it.grade !== undefined && it.grade !== '') ||
    (it.score !== null && it.score !== undefined);

  if (program === 'UG') {
    const filteredUgRows = ugRows.filter(s => {
      if (!studentSearch) return true;
      const q = studentSearch.toLowerCase();
      return s.name.toLowerCase().includes(q) || (s.registrationNumber || '').toLowerCase().includes(q);
    });
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <Card>
          <SectionHeader icon={<FaClipboardList />} title="UG Mini-Module Completion" color={MAROON} />
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div style={{ flex:'1 1 160px', display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Batch</label>
              <select value={ugBatchFilter} onChange={e => setUgBatchFilter(e.target.value)} style={sel}>
                <option value=''>All Batches</option>
                {batches.map(b => <option key={b._id} value={b._id}>{b.batchLabel}</option>)}
              </select>
            </div>
            <div style={{ flex:'2 1 220px' }}>
              <SearchInput value={studentSearch} onChange={setStudentSearch} placeholder="Search student by name or registration number…" />
            </div>
          </div>
        </Card>

        {ugLoading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:50 }}><Spinner /></div>
        ) : filteredUgRows.length === 0 ? (
          <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No UG students found.</p></Card>
        ) : (
          <Card style={{ padding:0, overflow:'hidden' }}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>
                    <th style={{ background:'#f8f9fa', padding:'10px 12px', textAlign:'left', fontWeight:700, color:'#555', borderBottom:'2px solid #e9ecef', fontSize:11, position:'sticky', left:0 }}>Student</th>
                    <th style={{ background:'#f8f9fa', padding:'10px 12px', textAlign:'left', fontWeight:700, color:'#555', borderBottom:'2px solid #e9ecef', fontSize:11 }}>Batch</th>
                    {ugModules.map(m => (
                      <th key={m.key} style={{ background:'#f8f9fa', padding:'10px 10px', textAlign:'center', fontWeight:700, color:'#555', borderBottom:'2px solid #e9ecef', fontSize:10.5, whiteSpace:'nowrap' }}>{m.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUgRows.map(s => (
                    <tr key={s._id} style={{ borderBottom:'1px solid #f0f0f0' }}>
                      <td style={{ padding:'8px 12px', fontWeight:600, whiteSpace:'nowrap' }}>{s.name}<div style={{ fontSize:10, color:'#999' }}>{s.registrationNumber}</div></td>
                      <td style={{ padding:'8px 12px', fontSize:11, color:'#777', whiteSpace:'nowrap' }}>{s.batchLabel}</td>
                      {ugModules.map(m => {
                        const cell = s.cells?.[m.key] || { count: 0, status: 'none' };
                        const color = cell.count === 0 ? '#bbb' : cell.status === 'approved' || cell.status === 'completed' ? '#27AE60' : cell.status === 'needs_revision' || cell.status === 'rejected' ? '#E74C3C' : '#F39C12';
                        return (
                          <td key={m.key} style={{ padding:'8px 10px', textAlign:'center' }}>
                            {cell.count === 0 ? <span style={{ color:'#ddd' }}>—</span> : <Badge label={cell.count} color='#fff' bg={color} />}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <Card>
        <SectionHeader icon={<FaClipboardList />} title="Student Grades" color={MAROON} />
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <div style={{ flex:'1 1 200px', display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Department</label>
            <select value={courseId} onChange={e => setCourseId(e.target.value)} style={sel}>
              <option value=''>— Select Department —</option>
              {departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
            </select>
          </div>
          <div style={{ flex:'1 1 160px', display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Batch</label>
            <select value={batch} onChange={e => setBatch(e.target.value)} style={sel} disabled={!courseId || batchLoad}>
              <option value=''>{batchLoad ? 'Loading…' : courseId ? '— Select Batch —' : '— Select dept first —'}</option>
              {batches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {studentsLoading && (
        <div style={{ display:'flex', justifyContent:'center', padding:50 }}><Spinner /></div>
      )}

      {!courseId && !studentsLoading && (
        <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>Select a department, then a batch, to see students.</p></Card>
      )}

      {courseId && !batch && !studentsLoading && (
        <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>Select a batch to see students.</p></Card>
      )}

      {courseId && batch && !studentsLoading && (
        <Card>
          <div style={{ marginBottom:14 }}>
            <SearchInput value={studentSearch} onChange={setStudentSearch} placeholder="Search student by name or registration number…" />
          </div>
          {filteredStudents.length === 0 ? (
            <p style={{ color:'#999', textAlign:'center', padding:20 }}>No students found in this batch.</p>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:10 }}>
              {filteredStudents.map(s => (
                <div
                  key={s._id}
                  onClick={() => openStudentGrades(s)}
                  style={{ border:'1px solid #e5e7eb', borderRadius:10, padding:'12px 14px', cursor:'pointer', background:'#fafafa', transition:'all 0.15s' }}
                >
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'#1a1a2e' }}>{s.name}</div>
                    {gradedStudentIds.has(s._id?.toString()) && (
                      <Badge label="Graded" color="#fff" bg="#27AE60" />
                    )}
                  </div>
                  <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{s.registrationNumber}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {gradesStudent && (
        <Modal title={`${gradesStudent.name} — Grades`} onClose={() => setGradesStudent(null)} wide>
          {gradesLoading ? (
            <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
              <div>
                <h4 style={{ margin:'0 0 10px', fontSize:14, color:'#1a1a2e' }}>Checklist Grades</h4>
                {checklistEvals.length === 0 ? (
                  <p style={{ color:'#999', fontSize:13 }}>No checklist grades recorded yet.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {checklistEvals.map(ce => (
                      <div key={ce._id} style={{ border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:6 }}>
                          <strong style={{ fontSize:13, color:'#1a1a2e' }}>{ce.checklistName}</strong>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {ce.grade && <Badge label={`Grade: ${ce.grade}`} bg={MAROON + '18'} color={MAROON} />}
                            <span style={{ fontSize:11, color:'#888' }}>by {teacherName(ce.evaluatedBy)}</span>
                          </div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:3, marginTop:8 }}>
                          {(ce.scores || []).map(item => (
                            <div key={item.itemId} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#444' }}>
                              <span>{item.itemName}</span>
                              <strong>{item.score}/10</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 style={{ margin:'0 0 10px', fontSize:14, color:'#1a1a2e' }}>Assessment Grades</h4>
                {assessmentSubs.length === 0 ? (
                  <p style={{ color:'#999', fontSize:13 }}>No assessment grades recorded yet.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {assessmentSubs.map(sub => {
                      const gradedPeriods = (sub.grades || [])
                        .map(period => ({ ...period, items: (period.items || []).filter(isGraded) }))
                        .filter(period => period.items.length > 0);
                      if (gradedPeriods.length === 0) return null;
                      return (
                        <div key={sub.submissionId} style={{ border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:6 }}>
                            <strong style={{ fontSize:13, color:'#1a1a2e' }}>{sub.assessmentName}</strong>
                            <span style={{ fontSize:11, color:'#888' }}>by {sub.teacherName}</span>
                          </div>
                          {gradedPeriods.map(period => (
                            <div key={period.periodId} style={{ marginTop:8 }}>
                              {gradedPeriods.length > 1 && (
                                <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:3 }}>{period.periodLabel}</div>
                              )}
                              <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                                {period.items.map(item => (
                                  <div key={item.itemId} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#444' }}>
                                    <span>{item.itemName}</span>
                                    <strong>{item.grade ?? item.score}</strong>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── Classic DRP — Students Tab ────────────────────────────────────────────────
const formatDrpDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const drpMiniBtn = { fontSize: 12, padding: '5px 10px', borderRadius: 8, background: '#fff7ed', color: '#b45309', border: '1px solid #fed7aa', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 600 };

// Current posting + past DRP history for one student — hospital/dates/certificate
// only, no attendance/diary/operations/public-health detail.
function DRPDetailView({ entry, student, onDownloadSystemCert, onDownloadUploadedCert, certDownloading }) {
  const studentId = student?.studentId || entry.student;
  const drpHistory = entry.drpHistory || [];
  const hasCurrent = !!entry.profile?.hospitalName;

  if (!hasCurrent && drpHistory.length === 0) {
    return <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>No DRP data recorded yet.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {hasCurrent && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FaHospital style={{ color: '#b11217' }} />
            <strong style={{ fontSize: 14 }}>Current DRP</strong>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, marginBottom: 10 }}>
            <div><span style={{ color: '#888', fontSize: 11, display: 'block' }}>Hospital</span>{entry.profile?.hospitalName || '—'}</div>
            <div><span style={{ color: '#888', fontSize: 11, display: 'block' }}>Duration</span>{formatDrpDate(entry.profile?.fromDate)} – {formatDrpDate(entry.profile?.toDate)}</div>
            <div>
              <span style={{ color: '#888', fontSize: 11, display: 'block' }}>Certificate</span>
              {entry.certificate?.issued
                ? <span style={{ color: '#059669' }}>System — Issued {formatDrpDate(entry.certificate.issuedAt)}</span>
                : entry.uploadedCertificate?.fileName
                  ? <span style={{ color: '#2563eb' }}>Uploaded — {formatDrpDate(entry.uploadedCertificate.uploadedAt)}</span>
                  : <span style={{ color: '#888' }}>Not issued</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {entry.certificate?.issued && (
              <button onClick={() => onDownloadSystemCert(entry.profile, student)} style={drpMiniBtn}>
                <FaDownload size={10} style={{ marginRight: 4 }} /> System Certificate
              </button>
            )}
            {entry.uploadedCertificate?.fileName && (
              <button onClick={() => onDownloadUploadedCert(studentId, null)} disabled={certDownloading === 'current'} style={{ ...drpMiniBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                {certDownloading === 'current' ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload size={10} style={{ marginRight: 4 }} />}
                Uploaded Certificate
              </button>
            )}
          </div>
        </div>
      )}

      {drpHistory.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FaHistory style={{ color: '#6366f1' }} />
            <strong style={{ fontSize: 14 }}>Past DRP History</strong>
            <span style={{ marginLeft: 'auto', background: '#ede9fe', color: '#7c3aed', fontSize: 12, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
              {drpHistory.length} past DRP{drpHistory.length > 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...drpHistory].reverse().map(hist => {
              const hasCert = hist.certificate?.issued || hist.uploadedCertificate?.fileName;
              return (
                <div key={hist._id} style={{ border: hasCert ? '1px solid #e5e7eb' : '1px solid #fca5a5', borderRadius: 10, padding: 12, background: hasCert ? '#fafafa' : '#fff9f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{hist.profile?.hospitalName || '—'}</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                        {formatDrpDate(hist.profile?.fromDate)} – {formatDrpDate(hist.profile?.toDate)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {hist.certificate?.issued && <Badge label="System Cert" color="#fff" bg="#d97706" />}
                      {hist.uploadedCertificate?.fileName && <Badge label="Uploaded Cert" color="#fff" bg="#2563eb" />}
                      {!hasCert && <Badge label="No Certificate" color="#fff" bg="#dc2626" />}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {hist.certificate?.issued && (
                      <button onClick={() => onDownloadSystemCert(hist.profile || hist, student)} style={drpMiniBtn}>
                        <FaDownload size={10} style={{ marginRight: 4 }} /> System Certificate
                      </button>
                    )}
                    {hist.uploadedCertificate?.fileName && (
                      <button onClick={() => onDownloadUploadedCert(studentId, hist._id)} disabled={certDownloading === hist._id} style={{ ...drpMiniBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                        {certDownloading === hist._id ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload size={10} style={{ marginRight: 4 }} />}
                        Uploaded Certificate
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DRPStudentsTab({ api, token, departments }) {
  const [courseId, setCourseId] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const [detailStudent, setDetailStudent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);
  const [certDownloading, setCertDownloading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const sel = { padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fafafa', minWidth: 0 };

  useEffect(() => {
    setLoading(true);
    const params = courseId ? `?courseId=${courseId}` : '';
    fetch(`${api}/monitor/drp-students${params}`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setStudents(d.students); })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => { setPage(1); }, [search, sortBy, courseId]);

  const filtered = students.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.registrationNumber || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'totalDRPs': return b.totalDRPs - a.totalDRPs;
      case 'completedDRPs': return b.completedDRPs - a.completedDRPs;
      case 'lastActivity': return new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0);
      default: return a.name.localeCompare(b.name);
    }
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const paged = sorted.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const openStudent = async (s) => {
    setDetailStudent(s);
    setDetailLoading(true);
    setDetailEntry(null);
    try {
      const r = await fetch(`${api}/monitor/drp-student/${s.studentId}`, { headers });
      const d = await r.json();
      if (d.success) setDetailEntry(d.entry);
    } catch (_) {}
    setDetailLoading(false);
  };

  const downloadSystemCert = async (profileData, studentInfo) => {
    try {
      // Current-DRP profile has no populated inchargeId here (unused field, kept
      // for legacy data only); drpHistory entries carry a resolved inchargeName
      // string directly, which is the only case that can render correctly.
      const inchargeName = typeof profileData?.inchargeName === 'string' ? profileData.inchargeName : '';
      await generateClassicDRPCertificate(
        {
          studentName: studentInfo?.name || '',
          courseName: studentInfo?.course || '',
          hospitalName: profileData?.hospitalName,
          fromDate: profileData?.fromDate,
          toDate: profileData?.toDate,
          inchargeName
        },
        collegeConfig.collegeName, collegeConfig.logo
      );
    } catch (_) {}
  };

  const downloadUploadedCert = async (studentId, historyId) => {
    const path = historyId ? `/drp-student/${studentId}/history/${historyId}/certificate` : `/drp-student/${studentId}/certificate`;
    const key = historyId || 'current';
    setCertDownloading(key);
    try {
      const r = await fetch(`${api}/monitor${path}`, { headers });
      const d = await r.json();
      if (d.success) await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
    } catch (_) {}
    setCertDownloading(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <SectionHeader icon={<FaHospital />} title="Classic DRP — Students" color="#E67E22" />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Department</label>
            <select value={courseId} onChange={e => setCourseId(e.target.value)} style={sel}>
              <option value=''>All Departments</option>
              {departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
            </select>
          </div>
          <div style={{ flex: '2 1 260px' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name or registration number…" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Sort by</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={sel}>
              <option value="name">Name (A–Z)</option>
              <option value="totalDRPs">Total DRPs</option>
              <option value="completedDRPs">Completed DRPs</option>
              <option value="lastActivity">Most Recent Activity</option>
            </select>
          </div>
        </div>
      </Card>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner /></div>
      ) : sorted.length === 0 ? (
        <Card><p style={{ textAlign: 'center', color: '#999', padding: 30 }}>No students found.</p></Card>
      ) : (
        <>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Student', 'Reg. No.', 'Course', 'Completed', 'Ongoing', 'Certificate', ''].map(h => (
                      <th key={h} style={{ background: '#f8f9fa', padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#555', borderBottom: '2px solid #e9ecef', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paged.map(s => (
                    <tr key={s.studentId} onClick={() => openStudent(s)} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{s.name}</td>
                      <td style={{ padding: '10px 14px', color: '#888', fontSize: 12 }}>{s.registrationNumber}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12 }}>{s.course}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <Badge label={s.completedDRPs} color="#fff" bg={s.completedDRPs > 0 ? '#27AE60' : '#bbb'} />
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <Badge label={s.ongoingDRPs > 0 ? 'Yes' : 'No'} color="#fff" bg={s.ongoingDRPs > 0 ? '#4A90E2' : '#bbb'} />
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        {s.currentCertified
                          ? <Badge label="Certified" color="#fff" bg={MAROON} />
                          : <span style={{ color: '#bbb', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <FaChevronRight style={{ color: '#bbb', fontSize: 12 }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '7px 12px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, color: page === 1 ? '#ccc' : '#555', cursor: page === 1 ? 'default' : 'pointer' }}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .map((p, i, arr) => (
                  <React.Fragment key={p}>
                    {i > 0 && arr[i - 1] !== p - 1 && <span style={{ color: '#ccc', fontSize: 12 }}>…</span>}
                    <button
                      onClick={() => setPage(p)}
                      style={{
                        width: 32, height: 32, border: '1px solid ' + (p === page ? MAROON : '#e5e7eb'),
                        background: p === page ? MAROON : '#fff',
                        color: p === page ? '#fff' : '#555',
                        borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer'
                      }}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: '7px 12px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, color: page === totalPages ? '#ccc' : '#555', cursor: page === totalPages ? 'default' : 'pointer' }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {detailStudent && (
        <Modal title={`${detailStudent.name} — DRP History`} onClose={() => { setDetailStudent(null); setDetailEntry(null); }} wide>
          {detailLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
          ) : !detailEntry ? (
            <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>No DRP data recorded yet.</p>
          ) : (
            <DRPDetailView
              entry={detailEntry}
              student={detailStudent}
              onDownloadSystemCert={downloadSystemCert}
              onDownloadUploadedCert={downloadUploadedCert}
              certDownloading={certDownloading}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── UG Overview Tab ────────────────────────────────────────────────────────
function UGOverviewTab({ api, token, onViewStudents }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topStudents, setTopStudents] = useState([]);

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch(`${api}/monitor/ug-overview`, { headers }).then(r => r.json()),
      fetch(`${api}/monitor/ug-logbook-trend`, { headers }).then(r => r.json()),
      fetch(`${api}/monitor/ug-student-activity`, { headers }).then(r => r.json()),
    ]).then(([ovD, trendD, stuD]) => {
      if (!active) return;
      if (ovD.success) setOverview(ovD.overview);
      if (trendD.success) setTrend(trendD.trend);
      if (stuD.success) setTopStudents(stuD.students);
    }).catch(() => {});
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  if (!overview) return <Spinner />;

  const miniModuleTotal = Object.values(overview.miniModuleActivity || {}).reduce((a, b) => a + b, 0);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div className="mon-grid-3">
        <StatCard icon={<FaUserGraduate />} label="UG Students" value={overview.totalUGStudents} color="#4A90E2" sub={`${overview.activeUGStudents} active`} />
        <StatCard icon={<FaChalkboardTeacher />} label="UG Faculty" value={overview.totalUGTeachers} color="#9B59B6" />
        <StatCard icon={<FaUniversity />} label="Subjects" value={overview.totalUGSubjects} color="#27AE60" sub={`${overview.totalUGBatches} batches`} />
        <StatCard icon={<FaBookMedical />} label="Competency Logs" value={overview.totalCompetencyLogs} color="#E67E22" sub={`${overview.pendingLogs} pending`} />
        <StatCard icon={<FaCheckCircle />} label="Approved" value={overview.approvedLogs} color="#1ABC9C" sub={`${overview.approvalRate}% rate`} />
        <StatCard icon={<FaClipboardList />} label="Mini-Module Entries" value={miniModuleTotal} color={MAROON} sub="AETCOM, SDL, DOAP, etc." />
      </div>

      <Card>
        <SectionHeader icon={<FaChartBar />} title="Competency Log Submission Trend (12 Months)" color={MAROON} />
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={trend} margin={{ top:5, right:10, left:-14, bottom:0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize:11 }} />
            <YAxis tick={{ fontSize:11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize:12 }} />
            <Bar dataKey="submitted" name="Submitted" fill="#4A90E2" radius={[4,4,0,0]} />
            <Bar dataKey="approved" name="Approved" fill="#27AE60" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {topStudents.length > 0 && (
        <Card>
          <SectionHeader icon={<FaStar />} title="Top UG Students by Submissions" color="#F39C12"
            right={<button onClick={onViewStudents} style={{ fontSize:12, color:MAROON, background:'none', border:'none', cursor:'pointer', fontWeight:700, display:'flex', alignItems:'center', gap:4 }}>View All <FaChevronRight /></button>}
          />
          <div className="scroll-x">
            <table className="mon-table">
              <thead><tr><th>#</th><th>Name</th><th>Reg. No.</th><th>Submitted</th><th>Approved</th><th>Rate</th></tr></thead>
              <tbody>
                {topStudents.slice(0,8).map((s,i) => (
                  <tr key={s._id || i}>
                    <td style={{ color:i<3?'#F39C12':'#aaa', fontWeight:i<3?700:400, fontSize:12 }}>{i+1}</td>
                    <td style={{ fontWeight:600 }}>{s.name||'—'}</td>
                    <td style={{ fontSize:12, color:'#777' }}>{s.registrationNumber||'—'}</td>
                    <td><Badge label={s.total} color="#4A90E2" bg="#4A90E215" /></td>
                    <td><Badge label={s.approved} color="#27AE60" bg="#27AE6015" /></td>
                    <td><Badge label={`${s.approvalRate}%`} color='#fff' bg={rateColor(s.approvalRate)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── UG Departments ("Subjects") Tab ────────────────────────────────────────
function UGDepartmentsTab({ api, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;
    fetch(`${api}/monitor/ug-departments`, { headers }).then(r => r.json()).then(d => {
      if (active && d.success) setSubjects(d.departments);
      if (active) setLoading(false);
    }).catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  if (loading) return <Spinner />;

  const filtered = subjects.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search subjects…" />
      </div>
      {filtered.length === 0 && (
        <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No UG subjects found.</p></Card>
      )}
      {filtered.map((subject, i) => {
        const c = COLORS[i % COLORS.length];
        return (
          <Card key={subject.subjectId} style={{ border:`1px solid ${c}30`, overflow:'hidden', padding:0 }}>
            <div style={{ background:c+'12', borderLeft:`5px solid ${c}`, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
              <div>
                <span style={{ fontWeight:800, fontSize:15, color:'#1a1a2e' }}>{subject.name}</span>
                {subject.code && <span style={{ marginLeft:8, fontSize:11, color:'#888', background:'#fff', padding:'2px 8px', borderRadius:20 }}>{subject.code}</span>}
              </div>
              <div style={{ display:'flex', gap:10, fontSize:12, flexWrap:'wrap' }}>
                <span><strong style={{ color:'#4A90E2', fontSize:16 }}>{subject.studentCount}</strong> <span style={{ color:'#888' }}>eligible students</span></span>
                <span><strong style={{ color:'#9B59B6', fontSize:16 }}>{subject.teacherCount}</strong> <span style={{ color:'#888' }}>teachers</span></span>
                <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <strong style={{ color:rateColor(subject.approvalRate) }}>{subject.approvalRate}%</strong> <span style={{ color:'#888' }}>approval</span>
                </span>
              </div>
            </div>
            <div style={{ padding:'12px 16px' }}>
              <div style={{ display:'flex', gap:20, fontSize:12, color:'#555', flexWrap:'wrap', marginBottom:12 }}>
                <span>📚 <strong>{subject.logbookCount}</strong> competency logs</span>
                <span>✅ <strong>{subject.approvedCount}</strong> approved</span>
              </div>
              <div style={{ marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#888', marginBottom:4 }}>
                  <span>Approval Progress</span>
                  <span style={{ fontWeight:700, color:rateColor(subject.approvalRate) }}>{subject.approvalRate}%</span>
                </div>
                <ProgressBar value={subject.approvalRate} color={rateColor(subject.approvalRate)} height={8} />
              </div>
              {subject.teachers.length > 0 && (
                <div>
                  <div style={{ fontSize:11, color:'#888', fontWeight:700, marginBottom:6 }}>FACULTY</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {subject.teachers.map((t, ti) => (
                      <span key={ti} style={{ fontSize:11, padding:'4px 10px', borderRadius:20, background: t.isHOD ? MAROON+'18' : '#f0f0f0', color: t.isHOD ? MAROON : '#555', border:`1px solid ${t.isHOD ? MAROON+'40' : '#e0e0e0'}`, fontWeight: t.isHOD ? 700 : 400 }}>
                        {t.isHOD ? '★ ' : ''}{t.name}{t.designation ? ` (${t.designation})` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ─── UG Students Tab ────────────────────────────────────────────────────────
function UGStudentsTab({ api, token, onSelectStudent }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const [rptYear, setRptYear] = useState('');
  const [rptMonth, setRptMonth] = useState('');
  const [rptBatch, setRptBatch] = useState('');
  const [rptLoading, setRptLoading] = useState(false);
  const [rptData, setRptData] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch(`${api}/ug/batches`, { headers }).then(r => r.json()),
      fetch(`${api}/monitor/ug-all-students`, { headers }).then(r => r.json()),
    ]).then(([batchD, stuD]) => {
      if (!active) return;
      if (batchD.success) setBatches(batchD.batches);
      if (stuD.success) setStudents(stuD.students);
      setLoading(false);
    }).catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  useEffect(() => { setPage(1); }, [search, batchFilter]);

  const filtered = students.filter(s => {
    if (batchFilter && s.batchId?.toString() !== batchFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!s.name.toLowerCase().includes(q) && !(s.registrationNumber||'').toLowerCase().includes(q) && !s.email.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);
  const yearOptions = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

  const handleViewReport = async () => {
    setRptLoading(true);
    const params = new URLSearchParams();
    if (rptYear) params.set('year', rptYear);
    if (rptMonth) params.set('month', rptMonth);
    if (rptBatch) params.set('batchId', rptBatch);
    try {
      const r = await fetch(`${api}/monitor/ug-student-report?${params}`, { headers });
      const d = await r.json();
      if (d.success) setRptData(d);
    } catch (_) {}
    setRptLoading(false);
  };

  const handleDownloadReport = async () => {
    setRptLoading(true);
    try {
      const params = new URLSearchParams();
      if (rptYear) params.set('year', rptYear);
      if (rptMonth) params.set('month', rptMonth);
      if (rptBatch) params.set('batchId', rptBatch);
      const r = await fetch(`${api}/monitor/ug-student-report?${params}`, { headers });
      const d = await r.json();
      if (!d.success) throw new Error('Failed to fetch report data.');
      setRptData(d);
      const batchLabel = batches.find(b => b._id === rptBatch)?.batchLabel;
      await generateStudentActivityReport({
        data: {
          periodLabel: d.periodLabel,
          activeStudents: d.activeStudents.map(s => ({ ...s, course: '—' })),
          inactiveStudents: d.inactiveStudents.map(s => ({ ...s, course: '—' })),
        },
        collegeName: collegeConfig.collegeName,
        logoSrc: collegeConfig.logo,
        filters: { batch: batchLabel || '' }
      });
    } catch (e) {
      alert('PDF generation failed: ' + e.message);
    }
    setRptLoading(false);
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <Card style={{ border:`1px solid ${MAROON}25`, background:`${MAROON}06` }}>
        <SectionHeader icon={<FaDownload />} title="Download UG Student Activity Report" color={MAROON} />
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Year</label>
            <select value={rptYear} onChange={e => setRptYear(e.target.value)} style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:110 }}>
              <option value=''>All Years</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Month</label>
            <select value={rptMonth} onChange={e => setRptMonth(e.target.value)} style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:130 }}>
              <option value=''>All Months</option>
              {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Batch</label>
            <select value={rptBatch} onChange={e => setRptBatch(e.target.value)} style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:150 }}>
              <option value=''>All Batches</option>
              {batches.map(b => <option key={b._id} value={b._id}>{b.batchLabel}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', gap:8, alignSelf:'flex-end' }}>
            <button onClick={handleViewReport} disabled={rptLoading} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', background: rptLoading ? '#ccc' : '#4A90E2', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor: rptLoading ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
              <FaChartBar /> {rptLoading ? 'Loading…' : 'View'}
            </button>
            <button onClick={handleDownloadReport} disabled={rptLoading} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', background: rptLoading ? '#ccc' : MAROON, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor: rptLoading ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
              <FaDownload /> {rptLoading ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
        </div>

        {rptData && (
          <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
              <span style={{ fontSize:13, fontWeight:700, color:MAROON }}>Period: {rptData.periodLabel}</span>
              <button onClick={() => setRptData(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#aaa', fontSize:18, lineHeight:1 }}><FaTimes /></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10 }}>
              <StatCard icon={<FaUserGraduate />} label="Active Students" value={rptData.activeStudents.length} color="#27AE60" />
              <StatCard icon={<FaUsers />} label="Inactive Students" value={rptData.inactiveStudents.length} color="#E74C3C" />
              <StatCard icon={<FaBookMedical />} label="Total Entries" value={rptData.activeStudents.reduce((s,r)=>s+r.entryCount,0)} color="#E67E22" />
            </div>
            <Card>
              <SectionHeader icon={<FaCheckCircle />} title={`Active Students — ${rptData.activeStudents.length}`} color="#27AE60" />
              {rptData.activeStudents.length === 0 ? (
                <p style={{ color:'#999', textAlign:'center', padding:16, fontSize:13 }}>No active students for this period.</p>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead><tr>{['#','Name','Reg. No.','Batch','Entries'].map(h => (
                      <th key={h} style={{ background:'#f0faf4', padding:'8px 10px', textAlign:h==='Name'?'left':'center', fontWeight:700, color:'#27AE60', borderBottom:'2px solid #c3e6cb', fontSize:11 }}>{h}</th>
                    ))}</tr></thead>
                    <tbody>
                      {rptData.activeStudents.map((s,i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #f0f0f0', background: i%2===0?'#fff':'#f9fef9' }}>
                          <td style={{ padding:'7px 10px', textAlign:'center', color:i<3?'#F39C12':'#aaa', fontWeight:i<3?700:400 }}>{i+1}</td>
                          <td style={{ padding:'7px 10px', fontWeight:600 }}>{s.name}</td>
                          <td style={{ padding:'7px 10px', textAlign:'center', color:'#888', fontSize:11 }}>{s.registrationNumber}</td>
                          <td style={{ padding:'7px 10px', textAlign:'center', fontSize:11 }}>{s.batch}</td>
                          <td style={{ padding:'7px 10px', textAlign:'center', fontWeight:800, color:'#27AE60', fontSize:14 }}>{s.entryCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
      </Card>

      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name, reg. no. or email…" />
        <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)} style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:150 }}>
          <option value=''>All Batches</option>
          {batches.map(b => <option key={b._id} value={b._id}>{b.batchLabel}</option>)}
        </select>
      </div>

      <div style={{ fontSize:13, color:'#777', fontWeight:600 }}>
        Showing {filtered.length === 0 ? 0 : (page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, filtered.length)} of {filtered.length} UG students
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.length === 0 && <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No UG students found.</p></Card>}
        {paged.map(s => {
          const rc = rateColor(s.approvalRate);
          return (
            <Card key={s._id} hoverable onClick={() => onSelectStudent(s._id)} style={{ padding:'14px 16px', cursor:'pointer' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div style={{ width:42, height:42, borderRadius:'50%', background:MAROON+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:MAROON, fontWeight:700, flexShrink:0 }}>
                  {(s.name||'?')[0].toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:14, color:'#1a1a2e' }}>{s.name}</span>
                    {s.registrationNumber && <span style={{ fontSize:11, color:'#888', background:'#f0f0f0', padding:'2px 7px', borderRadius:20 }}>{s.registrationNumber}</span>}
                    <span style={{ fontSize:11, color:MAROON, background:MAROON+'15', padding:'2px 7px', borderRadius:20, fontWeight:700 }}>{s.batchLabel}</span>
                    {!s.isActive && <Badge label="Inactive" color='#fff' bg='#E74C3C' />}
                  </div>
                  <div style={{ fontSize:11, color:'#888', marginTop:3 }}>{s.email}</div>
                </div>
                <div style={{ display:'flex', gap:14, alignItems:'center', flexShrink:0 }}>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'#4A90E2' }}>{s.totalLogs}</div>
                    <div style={{ fontSize:10, color:'#aaa' }}>Logs</div>
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:rc }}>{s.approvalRate}%</div>
                    <div style={{ fontSize:10, color:'#aaa' }}>Approved</div>
                  </div>
                  <FaChevronRight style={{ color:'#ccc', fontSize:13 }} />
                </div>
              </div>
              <div style={{ marginTop:10 }}>
                <ProgressBar value={s.approvalRate} color={rc} height={5} />
              </div>
            </Card>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:6 }}>
          <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1} style={{ padding:'7px 12px', border:'1px solid #e5e7eb', background:'#fff', borderRadius:8, fontSize:12, fontWeight:600, color: page===1?'#ccc':'#555', cursor: page===1?'default':'pointer' }}>Prev</button>
          <span style={{ fontSize:12, color:'#555' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages} style={{ padding:'7px 12px', border:'1px solid #e5e7eb', background:'#fff', borderRadius:8, fontSize:12, fontWeight:600, color: page===totalPages?'#ccc':'#555', cursor: page===totalPages?'default':'pointer' }}>Next</button>
        </div>
      )}
    </div>
  );
}

// ─── UG Teachers Tab ────────────────────────────────────────────────────────
function UGTeachersTab({ api, token, onSelectTeacher }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [rptYear, setRptYear] = useState('');
  const [rptMonth, setRptMonth] = useState('');
  const [rptLoading, setRptLoading] = useState(false);
  const [rptData, setRptData] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${api}/monitor/ug-teacher-activity`, { headers }).then(r => r.json()).then(d => {
      if (!active) return;
      if (d.success) setTeachers(d.teachers);
      setLoading(false);
    }).catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  const filtered = teachers.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.email.toLowerCase().includes(q) || (t.department||'').toLowerCase().includes(q);
  });
  const yearOptions = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

  const handleDownloadReport = async () => {
    setRptLoading(true);
    try {
      const params = new URLSearchParams();
      if (rptYear) params.set('year', rptYear);
      if (rptMonth) params.set('month', rptMonth);
      const r = await fetch(`${api}/monitor/ug-teacher-report?${params}`, { headers });
      const d = await r.json();
      if (!d.success) throw new Error('Failed to fetch report data.');
      setRptData(d);
      await generateFacultyActivityReport({
        data: {
          periodLabel: d.periodLabel,
          activeTeachers: d.activeTeachers.map(t => ({ ...t, courses: [], avgTurnaroundDays: null })),
          inactiveTeachers: d.inactiveTeachers.map(t => ({ ...t, courses: [] })),
        },
        collegeName: collegeConfig.collegeName,
        logoSrc: collegeConfig.logo,
        filters: {}
      });
    } catch (e) {
      alert('PDF generation failed: ' + e.message);
    }
    setRptLoading(false);
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <Card style={{ border:`1px solid ${MAROON}25`, background:`${MAROON}06` }}>
        <SectionHeader icon={<FaDownload />} title="Download UG Faculty Activity Report" color={MAROON} />
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Year</label>
            <select value={rptYear} onChange={e => setRptYear(e.target.value)} style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:110 }}>
              <option value=''>All Years</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Month</label>
            <select value={rptMonth} onChange={e => setRptMonth(e.target.value)} style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:130 }}>
              <option value=''>All Months</option>
              {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
          <button onClick={handleDownloadReport} disabled={rptLoading} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', background: rptLoading ? '#ccc' : MAROON, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor: rptLoading ? 'not-allowed' : 'pointer', whiteSpace:'nowrap', alignSelf:'flex-end' }}>
            <FaDownload /> {rptLoading ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
        {rptData && (
          <p style={{ marginTop:10, fontSize:12, color:'#666' }}>
            Generated report for <strong>{rptData.periodLabel}</strong> — {rptData.activeTeachers.length} active, {rptData.inactiveTeachers.length} inactive.
          </p>
        )}
      </Card>

      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name, email, or department…" />
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.length === 0 && <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No UG faculty found.</p></Card>}
        {filtered.map(t => {
          return (
            <Card key={t._id} hoverable onClick={() => onSelectTeacher(t._id)} style={{ padding:'14px 16px', cursor:'pointer' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div style={{ width:42, height:42, borderRadius:'50%', background:MAROON+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:MAROON, fontWeight:700, flexShrink:0 }}>
                  {(t.name||'?')[0].toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:14, color:'#1a1a2e' }}>{t.name}</span>
                    {t.isHOD && <Badge label="HOD" color='#fff' bg={MAROON} />}
                    {t.leavingDate && <span style={{ fontSize:11, color:'#b45309' }}>(left)</span>}
                  </div>
                  <div style={{ fontSize:11, color:'#888', marginTop:3 }}>
                    {t.subjects.join(' · ') || 'No subjects assigned'}
                  </div>
                </div>
                <div style={{ display:'flex', gap:14, alignItems:'center', flexShrink:0 }}>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'#27AE60' }}>{t.reviewed}</div>
                    <div style={{ fontSize:10, color:'#aaa' }}>Reviewed</div>
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'#F39C12' }}>{t.pending}</div>
                    <div style={{ fontSize:10, color:'#aaa' }}>Pending</div>
                  </div>
                  <FaChevronRight style={{ color:'#ccc', fontSize:13 }} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── UG Team Tab (Subject → Teachers → Batches) ────────────────────────────
function UGTeamTab({ api, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [map, setMap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;
    fetch(`${api}/monitor/ug-teacher-subject-map`, { headers }).then(r => r.json()).then(d => {
      if (!active) return;
      if (d.success) setMap(d.map);
      setLoading(false);
    }).catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  if (loading) return <Spinner />;

  const filtered = map.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.subjectName.toLowerCase().includes(q) || s.teachers.some(t => t.name.toLowerCase().includes(q));
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by subject or faculty…" />
      </div>
      {filtered.length === 0 && (
        <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No UG subjects found.</p></Card>
      )}
      {filtered.map((s, i) => {
        const c = COLORS[i % COLORS.length];
        return (
          <Card key={s.subjectId} style={{ border:`1px solid ${c}30`, overflow:'hidden', padding:0 }}>
            <div style={{ background:c+'12', borderLeft:`5px solid ${c}`, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
              <div>
                <span style={{ fontWeight:800, fontSize:15, color:'#1a1a2e' }}>{s.subjectName}</span>
                {s.subjectCode && <span style={{ marginLeft:8, fontSize:11, color:'#888', background:'#fff', padding:'2px 8px', borderRadius:20 }}>{s.subjectCode}</span>}
              </div>
              <span style={{ fontSize:12 }}><strong style={{ color:'#4A90E2', fontSize:16 }}>{s.studentCount}</strong> <span style={{ color:'#888' }}>eligible students</span></span>
            </div>
            <div style={{ padding:'12px 16px' }}>
              <div style={{ fontSize:11, color:'#888', fontWeight:700, marginBottom:6 }}>ASSIGNED FACULTY</div>
              {s.teachers.length === 0 ? (
                <p style={{ margin:0, fontSize:12, color:'#bbb', fontStyle:'italic' }}>No faculty assigned to this subject.</p>
              ) : (
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
                  {s.teachers.map((t, ti) => (
                    <span key={ti} style={{ fontSize:11, padding:'4px 10px', borderRadius:20, background:'#f0f0f0', color:'#555', border:'1px solid #e0e0e0' }}>
                      {t.name}{t.designation ? ` (${t.designation})` : ''}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ fontSize:11, color:'#888', fontWeight:700, marginBottom:6 }}>ACTIVE BATCHES</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {s.batches.length === 0
                  ? <span style={{ fontSize:12, color:'#bbb', fontStyle:'italic' }}>No active batches.</span>
                  : s.batches.map((b, bi) => (
                    <span key={bi} style={{ fontSize:11, padding:'4px 10px', borderRadius:20, background:MAROON+'10', color:MAROON, fontWeight:600 }}>{b}</span>
                  ))}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Main MonitorDashboard ─────────────────────────────────────────────────────
export default function MonitorDashboard() {
  const { user, logout } = useAuth();
  const token = localStorage.getItem('token');
  const api = collegeConfig.api.baseURL;

  const [tab, setTab] = useState('overview');
  const [program, setProgram] = useState('PG'); // 'PG' | 'UG' — global switch, see monitorUgHelpers section below
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Data
  const [overview, setOverview] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [trend, setTrend] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [allStudentsLoading, setAllStudentsLoading] = useState(false);
  const [teacherMap, setTeacherMap] = useState([]);
  const [teacherMapLoading, setTeacherMapLoading] = useState(false);
  const [batchLabels, setBatchLabels] = useState({});

  // Drill-down modals
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [viewStudentsTeacherId, setViewStudentsTeacherId] = useState(null);

  // Per-student logbook PDF download
  const [downloadStudent, setDownloadStudent] = useState(null);
  const [downloadingStudentId, setDownloadingStudentId] = useState(null);

  // Bulk (whole-batch) logbook PDF download
  const [bulkCourseId, setBulkCourseId] = useState('');
  const [bulkBatch, setBulkBatch] = useState('');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { current, total, name }

  // Filters
  const [studentSearch, setStudentSearch] = useState('');
  const [studentCourseFilter, setStudentCourseFilter] = useState('');
  const [studentBatchFilter, setStudentBatchFilter] = useState('');
  const [studentsPage, setStudentsPage] = useState(1);
  const STUDENTS_PER_PAGE = 20;
  const [teacherSearch, setTeacherSearch] = useState('');
  const [teacherDeptFilter, setTeacherDeptFilter] = useState('');
  const [teacherDesignationFilter, setTeacherDesignationFilter] = useState('');
  const [teachersPage, setTeachersPage] = useState(1);
  const TEACHERS_PER_PAGE = 20;
  const [deptSearch, setDeptSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');

  // Student report filters & results
  const [rptMonth, setRptMonth] = useState('');
  const [rptYear, setRptYear] = useState('');
  const [rptBatch, setRptBatch] = useState('');
  const [rptCourse, setRptCourse] = useState('');
  const [rptLoading, setRptLoading] = useState(false);
  const [rptData, setRptData] = useState(null);

  // Faculty report filters & results
  const [rptTeacherMonth, setRptTeacherMonth] = useState('');
  const [rptTeacherYear, setRptTeacherYear] = useState('');
  const [rptTeacherCourse, setRptTeacherCourse] = useState('');
  const [rptTeacherLoading, setRptTeacherLoading] = useState(false);
  const [rptTeacherData, setRptTeacherData] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [ovR, deptR, trendR, teachR, stuR] = await Promise.all([
        fetch(`${api}/monitor/overview`, { headers }),
        fetch(`${api}/monitor/departments`, { headers }),
        fetch(`${api}/monitor/logbook-trend`, { headers }),
        fetch(`${api}/monitor/teacher-activity`, { headers }),
        fetch(`${api}/monitor/student-activity`, { headers }),
      ]);
      const [ovD, deptD, trendD, teachD, stuD] = await Promise.all([
        ovR.json(), deptR.json(), trendR.json(), teachR.json(), stuR.json()
      ]);
      if (ovD.success) setOverview(ovD.overview);
      if (deptD.success) setDepartments(deptD.departments);
      if (trendD.success) setTrend(trendD.trend);
      if (teachD.success) setTeachers(teachD.teachers);
      if (stuD.success) setStudents(stuD.students);
      setLastUpdated(new Date());
    } catch (_) {}
    setLoading(false);
    setRefreshing(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  const fetchAllStudents = useCallback(async () => {
    setAllStudentsLoading(true);
    try {
      const r = await fetch(`${api}/monitor/all-students`, { headers });
      const d = await r.json();
      if (d.success) setAllStudents(d.students);
    } catch (_) {}
    setAllStudentsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  const fetchTeacherMap = useCallback(async () => {
    setTeacherMapLoading(true);
    try {
      const r = await fetch(`${api}/monitor/teacher-student-map`, { headers });
      const d = await r.json();
      if (d.success) setTeacherMap(d.map);
    } catch (_) {}
    setTeacherMapLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  useEffect(() => {
    fetch(`${api}/system-settings`)
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.batchLabels)) {
          setBatchLabels(Object.fromEntries(d.batchLabels.map(b => [b.batch, b.label])));
        }
      })
      .catch(() => {});
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'students' && program === 'PG' && allStudents.length === 0) fetchAllStudents(); }, [tab, program, allStudents.length, fetchAllStudents]);
  useEffect(() => { setStudentsPage(1); }, [studentSearch, studentCourseFilter, studentBatchFilter]);
  useEffect(() => { setBulkBatch(''); }, [bulkCourseId]);
  useEffect(() => {
    if (program === 'UG' && ['eligibility', 'drp'].includes(tab)) setTab('overview');
  }, [program, tab]);
  useEffect(() => { setTeachersPage(1); }, [teacherSearch, teacherDeptFilter, teacherDesignationFilter]);
  useEffect(() => {
    if (program === 'PG' && (tab === 'team' || tab === 'students' || tab === 'teachers') && teacherMap.length === 0) fetchTeacherMap();
  }, [tab, program, teacherMap.length, fetchTeacherMap]);

  const downloadStudentLogbookPdf = async (student, courseId, includeSubmissionDate) => {
    const r = await fetch(`${api}/monitor/student/${student._id}/logbook-entries?courseId=${courseId}`, { headers });
    const d = await r.json();
    if (!d.success) throw new Error(d.message || 'Failed to fetch logbook data.');

    const pdfEntries = (d.entries || []).filter(e => e.status !== 'draft');
    if (pdfEntries.length === 0) throw new Error('No submitted entries to generate PDF.');

    const [checklistRes, assessmentRes, drpRes] = await Promise.allSettled([
      fetch(`${api}/checklist-evaluations/student/${student._id}/evaluations`, { headers }).then(res => res.json()),
      fetch(`${api}/assessments/student/${student._id}/submissions`, { headers }).then(res => res.json()),
      fetch(`${api}/monitor/drp-student/${student._id}`, { headers }).then(res => res.json()),
    ]);
    const checklistEvaluations = checklistRes.status === 'fulfilled' && checklistRes.value.success
      ? checklistRes.value.evaluations || []
      : [];
    const assessmentSubmissions = assessmentRes.status === 'fulfilled' && assessmentRes.value.success
      ? assessmentRes.value.submissions || []
      : [];
    const drpEntry = drpRes.status === 'fulfilled' && drpRes.value.success
      ? drpRes.value.entry
      : null;

    await generateLogbookPDF(
      d.student?.name || student.name || 'Student',
      d.course?.name || '',
      d.course?.modules || [],
      pdfEntries,
      {
        collegeName: collegeConfig.collegeName,
        shortName: collegeConfig.shortName,
        logo: collegeConfig.logo,
        theme: collegeConfig.theme,
        copyrightYear: collegeConfig.copyrightYear,
        appName: collegeConfig.appName
      },
      {
        email: d.student?.email || student.email,
        profile: {
          registrationNumber: d.student?.registrationNumber || student.registrationNumber,
          batch: d.student?.batch || ''
        },
        additionalInfo: d.student?.additionalInfo || {}
      },
      { checklistEvaluations, assessmentSubmissions, drpEntry, includeSubmissionDate }
    );
  };

  const handleDownloadStudentLogbook = async (student, includeSubmissionDate) => {
    setDownloadStudent(null);
    setDownloadingStudentId(student._id);
    try {
      const courseId = student.courses?.[0]?.courseId;
      if (!courseId) throw new Error('This student has no assigned course.');
      await downloadStudentLogbookPdf(student, courseId, includeSubmissionDate);
    } catch (err) {
      alert(err.message || 'Failed to generate PDF.');
    } finally {
      setDownloadingStudentId(null);
    }
  };

  // Batch scoped to the selected department (or across all departments if none picked)
  const bulkBatchOptions = [...new Set(
    allStudents
      .flatMap(s => (s.courses || [])
        .filter(c => !bulkCourseId || c.courseId?.toString() === bulkCourseId)
        .map(c => c.batch))
      .filter(Boolean)
  )].sort();

  const bulkMatchedStudents = bulkBatch
    ? allStudents.filter(s => (s.courses || []).some(c =>
        c.batch === bulkBatch && (!bulkCourseId || c.courseId?.toString() === bulkCourseId)
      ))
    : [];

  const handleBulkDownloadBatch = async (includeSubmissionDate) => {
    setBulkConfirmOpen(false);
    const targets = bulkMatchedStudents;
    if (targets.length === 0) return;

    setBulkDownloading(true);
    const failed = [];
    for (let i = 0; i < targets.length; i++) {
      const student = targets[i];
      setBulkProgress({ current: i + 1, total: targets.length, name: student.name || 'Student' });
      try {
        const matchingCourse = (student.courses || []).find(c =>
          c.batch === bulkBatch && (!bulkCourseId || c.courseId?.toString() === bulkCourseId)
        );
        const courseId = matchingCourse?.courseId || student.courses?.[0]?.courseId;
        if (!courseId) throw new Error('No assigned course.');
        await downloadStudentLogbookPdf(student, courseId, includeSubmissionDate);
        // Space out downloads slightly so the browser doesn't treat them as a popup flood.
        await new Promise(res => setTimeout(res, 400));
      } catch (err) {
        failed.push(`${student.name || 'Student'}: ${err.message || 'failed'}`);
      }
    }
    setBulkDownloading(false);
    setBulkProgress(null);
    if (failed.length > 0) {
      alert(`Downloaded ${targets.length - failed.length} of ${targets.length} logbooks.\n\nFailed:\n${failed.join('\n')}`);
    }
  };

  const fetchStudentReport = async () => {
    const params = new URLSearchParams();
    if (rptMonth)  params.set('month',    rptMonth);
    if (rptYear)   params.set('year',     rptYear);
    if (rptBatch)  params.set('batch',    rptBatch);
    if (rptCourse) params.set('courseId', rptCourse);
    const r = await fetch(`${api}/monitor/student-report?${params}`, { headers });
    const d = await r.json();
    return d.success ? d : null;
  };

  const handleViewStudentReport = async () => {
    setRptLoading(true);
    try {
      const d = await fetchStudentReport();
      if (d) setRptData(d);
    } catch (_) {}
    setRptLoading(false);
  };

  const handleDownloadStudentReport = async () => {
    setRptLoading(true);
    try {
      const d = rptData || await fetchStudentReport();
      if (d) {
        if (!rptData) setRptData(d);
        const courseName = rptCourse ? (departments.find(dep => dep.courseId?.toString() === rptCourse)?.courseName || '') : '';
        await generateStudentActivityReport({
          data: d,
          collegeName: collegeConfig.college?.name || 'College',
          logoSrc: collegeConfig.college?.logo || null,
          filters: { course: courseName, batch: rptBatch },
        });
      }
    } catch (_) {}
    setRptLoading(false);
  };

  const fetchTeacherReport = async () => {
    const params = new URLSearchParams();
    if (rptTeacherMonth)  params.set('month',    rptTeacherMonth);
    if (rptTeacherYear)   params.set('year',     rptTeacherYear);
    if (rptTeacherCourse) params.set('courseId', rptTeacherCourse);
    const r = await fetch(`${api}/monitor/teacher-report?${params}`, { headers });
    const d = await r.json();
    return d.success ? d : null;
  };

  const handleViewTeacherReport = async () => {
    setRptTeacherLoading(true);
    try {
      const d = await fetchTeacherReport();
      if (d) setRptTeacherData(d);
    } catch (_) {}
    setRptTeacherLoading(false);
  };

  const handleDownloadTeacherReport = async () => {
    setRptTeacherLoading(true);
    try {
      const d = rptTeacherData || await fetchTeacherReport();
      if (d) {
        if (!rptTeacherData) setRptTeacherData(d);
        const courseName = rptTeacherCourse ? (departments.find(dep => dep.courseId?.toString() === rptTeacherCourse)?.courseName || '') : '';
        await generateFacultyActivityReport({
          data: d,
          collegeName: collegeConfig.college?.name || 'College',
          logoSrc: collegeConfig.college?.logo || null,
          filters: { course: courseName },
        });
      }
    } catch (_) {}
    setRptTeacherLoading(false);
  };

  const name = user ? `${user.profile?.firstName || user.name || ''} ${user.profile?.lastName || ''}`.trim() || 'Monitor' : 'Monitor';
  const isDeptMonitor = user?.role === 'department_monitor';
  const roleLabel = isDeptMonitor ? 'Department Monitor' : 'Analytics Monitor';
  // DRP and Exam Eligibility have no UG analog — hidden from the tab bar in UG mode.
  const visibleTabs = TABS.filter(t => program === 'PG' || !['eligibility', 'drp'].includes(t.id));

  // ── Derived options ──
  const batchOptions = [...new Set(
    allStudents.flatMap(s => (s.courses || []).map(c => c.batch).filter(Boolean))
  )].sort();

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const MONTHS = [
    { v:'1',label:'January' },{ v:'2',label:'February' },{ v:'3',label:'March' },
    { v:'4',label:'April' },{ v:'5',label:'May' },{ v:'6',label:'June' },
    { v:'7',label:'July' },{ v:'8',label:'August' },{ v:'9',label:'September' },
    { v:'10',label:'October' },{ v:'11',label:'November' },{ v:'12',label:'December' },
  ];

  // ── Filtered lists ──
  const filteredStudents = allStudents.filter(s => {
    const q = studentSearch.toLowerCase();
    const matchSearch = !q || s.name.toLowerCase().includes(q) || s.registrationNumber.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
    const matchCourse = !studentCourseFilter || (s.courses || []).some(c => c.courseId?.toString() === studentCourseFilter);
    const matchBatch = !studentBatchFilter || (s.courses || []).some(c => c.batch === studentBatchFilter);
    return matchSearch && matchCourse && matchBatch;
  });
  filteredStudents.sort((a, b) => {
    const ab = (a.courses || []).map(c => c.batch).filter(Boolean).sort()[0] || '';
    const bb = (b.courses || []).map(c => c.batch).filter(Boolean).sort()[0] || '';
    return ab.localeCompare(bb, undefined, { numeric: true, sensitivity: 'base' }) || a.name.localeCompare(b.name);
  });
  const studentsTotalPages = Math.max(1, Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE));
  const pagedStudents = filteredStudents.slice((studentsPage - 1) * STUDENTS_PER_PAGE, studentsPage * STUDENTS_PER_PAGE);

  const teacherDeptOptions = [...new Set(teachers.map(t => t.department).filter(Boolean))].sort();
  const teacherDesignationOptions = [...new Set(teachers.map(t => t.designation).filter(Boolean))].sort();

  const filteredTeachers = teachers.filter(t => {
    const q = teacherSearch.toLowerCase();
    const matchSearch = !q || t.name.toLowerCase().includes(q) || t.email.toLowerCase().includes(q) || t.courses.join(' ').toLowerCase().includes(q);
    const matchDept = !teacherDeptFilter || t.department === teacherDeptFilter;
    const matchDesignation = !teacherDesignationFilter || t.designation === teacherDesignationFilter;
    return matchSearch && matchDept && matchDesignation;
  });
  const teachersTotalPages = Math.max(1, Math.ceil(filteredTeachers.length / TEACHERS_PER_PAGE));
  const pagedTeachers = filteredTeachers.slice((teachersPage - 1) * TEACHERS_PER_PAGE, teachersPage * TEACHERS_PER_PAGE);

  const filteredDepts = departments.filter(d =>
    !deptSearch || d.courseName.toLowerCase().includes(deptSearch.toLowerCase())
  );

  const filteredTeacherMap = teacherMap.filter(row => {
    const q = teamSearch.toLowerCase();
    return !q || row.teacherName.toLowerCase().includes(q) || row.courseName.toLowerCase().includes(q) ||
      row.students.some(s => s.name.toLowerCase().includes(q));
  });

  // Group faculty↔student rows by department (course) for the Faculty ↔ Student Map tab
  const teacherMapByDept = Object.values(
    filteredTeacherMap.reduce((acc, row) => {
      const key = row.courseId?.toString() || row.courseName;
      if (!acc[key]) acc[key] = { courseId: row.courseId, courseName: row.courseName, rows: [] };
      acc[key].rows.push(row);
      return acc;
    }, {})
  ).sort((a, b) => a.courseName.localeCompare(b.courseName));

  // studentId -> [{ teacherName, teacherId, courseName }], derived from the
  // same teacher↔student selections used by the Faculty ↔ Student Map tab.
  const studentFacultyMap = {};
  // teacherId -> { studentIds:Set, students:[{...student, courseName}] }, deduped
  // across a teacher's courses so a student selected in two courses only counts once.
  const teacherStudentsMap = {};
  teacherMap.forEach(row => {
    const tid = row.teacherId?.toString();
    if (tid) {
      if (!teacherStudentsMap[tid]) teacherStudentsMap[tid] = { studentIds: new Set(), students: [] };
      row.students.forEach(s => {
        const sid = s._id?.toString();
        if (sid && !teacherStudentsMap[tid].studentIds.has(sid)) {
          teacherStudentsMap[tid].studentIds.add(sid);
          teacherStudentsMap[tid].students.push({ ...s, courseName: row.courseName });
        }
      });
    }
    row.students.forEach(s => {
      const sid = s._id?.toString();
      if (!sid) return;
      if (!studentFacultyMap[sid]) studentFacultyMap[sid] = [];
      studentFacultyMap[sid].push({ teacherName: row.teacherName, teacherId: row.teacherId, courseName: row.courseName });
    });
  });

  // ── Dept pie ──
  const deptPieData = departments.filter(d => d.studentCount > 0).map((d,i) => ({ name:d.courseName, value:d.studentCount, color:COLORS[i%COLORS.length] }));

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f5f6fa' }}>
        <div style={{ textAlign:'center' }}>
          <div style={{ width:48, height:48, border:`4px solid ${MAROON}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 14px' }} />
          <p style={{ color:'#666', margin:0, fontWeight:600 }}>Loading analytics…</p>
        </div>
        <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:'#f0f2f5', fontFamily:'system-ui,-apple-system,sans-serif' }}>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        .mon-grid-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
        .mon-grid-4 { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
        .mon-grid-auto { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:12px; }
        @media(max-width:700px) {
          .mon-grid-3 { grid-template-columns:repeat(2,1fr) !important; }
          .mon-grid-4 { grid-template-columns:repeat(2,1fr) !important; }
        }
        @media(max-width:400px) {
          .mon-grid-3 { grid-template-columns:1fr !important; }
          .mon-grid-4 { grid-template-columns:1fr !important; }
        }
        .mon-tab-scroll { display:flex; gap:6px; overflow-x:auto; padding-bottom:2px; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
        .mon-tab-scroll::-webkit-scrollbar { display:none; }
        .mon-table { width:100%; border-collapse:collapse; font-size:13px; }
        .mon-table th { background:#f8f9fa; padding:10px 12px; text-align:left; font-weight:700; color:#555; border-bottom:2px solid #e9ecef; font-size:12px; }
        .mon-table td { padding:10px 12px; border-bottom:1px solid #f0f0f0; color:#333; }
        .mon-table tr:last-child td { border-bottom:none; }
        .mon-table tr:hover td { background:#fafafa; }
        .scroll-x { overflow-x:auto; }
        .card-hover:hover { box-shadow:0 8px 28px rgba(0,0,0,0.13) !important; transform:translateY(-2px) !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ background:MAROON, color:'#fff', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, boxShadow:'0 2px 12px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src={collegeConfig.logo} alt="logo" style={{ height:34, width:34, borderRadius:6, objectFit:'contain', background:'#fff', padding:2 }} onError={e => { e.target.style.display='none'; }} />
          <div>
            <div style={{ fontWeight:800, fontSize:14 }}>{name}</div>
            <div style={{ fontSize:10, opacity:0.75 }}>{roleLabel} · {collegeConfig.shortName}</div>
            {isDeptMonitor && departments.length > 0 && (
              <div style={{ fontSize:10, opacity:0.75, marginTop:1 }}>{departments.map(d => d.courseName).join(', ')}</div>
            )}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {lastUpdated && <span style={{ fontSize:10, opacity:0.6 }}>{lastUpdated.toLocaleTimeString()}</span>}
          <button onClick={() => fetchAll(true)} disabled={refreshing} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', padding:'7px 12px', borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600 }}>
            <FaSync style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
            <span style={{ display:'none', ['@media(min-width:500px)']: { display:'inline' } }}>Refresh</span>
          </button>
          <button onClick={logout} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', padding:'7px 12px', borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600 }}>
            <FaSignOutAlt />
          </button>
        </div>
      </div>

      {/* ── Program toggle ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e9ecef', padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:12, fontWeight:700, color:'#888' }}>Program:</span>
        <div style={{ display:'flex', gap:4, background:'#f0f2f5', borderRadius:8, padding:3 }}>
          {['PG', 'UG'].map(p => (
            <button key={p} onClick={() => setProgram(p)} style={{
              padding:'6px 18px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:700,
              background: program === p ? MAROON : 'transparent',
              color: program === p ? '#fff' : '#666',
              transition:'all 0.15s'
            }}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e9ecef', padding:'0 16px', position:'sticky', top:58, zIndex:90, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <div className="mon-tab-scroll">
          {visibleTabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding:'12px 14px', border:'none', background:'transparent', cursor:'pointer', fontWeight: tab===t.id ? 700 : 500, fontSize:13,
              color: tab===t.id ? MAROON : '#666', borderBottom: tab===t.id ? `3px solid ${MAROON}` : '3px solid transparent',
              display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap', transition:'all 0.15s', flexShrink:0
            }}>
              <span style={{ fontSize:12 }}>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth:1140, margin:'0 auto', padding:'20px 14px 60px' }}>

        {/* ══════════════ OVERVIEW ══════════════ */}
        {tab === 'overview' && program === 'UG' && <UGOverviewTab api={api} token={token} onViewStudents={() => setTab('students')} />}
        {tab === 'overview' && program === 'PG' && overview && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

            {/* Stat grid */}
            <div className="mon-grid-3">
              <StatCard icon={<FaUserGraduate />} label="Total Students" value={overview.totalStudents} color="#4A90E2" sub={`${overview.activeStudents} active`} />
              <StatCard icon={<FaChalkboardTeacher />} label="Faculty" value={overview.totalTeachers} color="#9B59B6" sub={`${overview.hodCount} HODs`} />
              <StatCard icon={<FaUniversity />} label="Courses" value={overview.totalCourses} color="#27AE60" />
              <StatCard icon={<FaBookMedical />} label="Logbook Entries" value={overview.totalLogbookEntries} color="#E67E22" sub={`${overview.pendingReview} pending`} />
              <StatCard icon={<FaCheckCircle />} label="Approved" value={overview.approvedEntries} color="#1ABC9C" sub={`${overview.approvalRate}% rate`} />
              <StatCard icon={<FaHospital />} label="DRP Students" value={overview.totalClassicDRP} color={MAROON} sub={`${overview.certifiedDRP} certified`} />
            </div>

            {/* Charts row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:16 }}>
              <Card>
                <SectionHeader icon={<FaChartBar />} title="Logbook Submission Trend (12 Months)" color={MAROON} />
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={trend} margin={{ top:5, right:10, left:-14, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize:11 }} />
                    <YAxis tick={{ fontSize:11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize:12 }} />
                    <Bar dataKey="submitted" name="Submitted" fill="#4A90E2" radius={[4,4,0,0]} />
                    <Bar dataKey="approved" name="Approved" fill="#27AE60" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              {deptPieData.length > 0 && (
                <Card>
                  <SectionHeader icon={<FaChartPie />} title="Student Distribution by Department" color={MAROON} />
                  <div style={{ display:'flex', flexWrap:'wrap', gap:16, alignItems:'center', justifyContent:'center' }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={deptPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                          label={({ name, percent }) => `${name.length>12?name.slice(0,12)+'…':name} ${(percent*100).toFixed(0)}%`}
                        >
                          {deptPieData.map((e,i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip formatter={(v,n) => [v,n]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 14px', justifyContent:'center' }}>
                      {deptPieData.map((d,i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                          <span style={{ width:10, height:10, borderRadius:'50%', background:d.color, display:'inline-block', flexShrink:0 }} />
                          <span style={{ color:'#444' }}>{d.name}: <strong>{d.value}</strong></span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Top students quick glance */}
            {students.length > 0 && (
              <Card>
                <SectionHeader icon={<FaStar />} title="Top Students by Submissions" color="#F39C12"
                  right={<button onClick={() => setTab('students')} style={{ fontSize:12, color:MAROON, background:'none', border:'none', cursor:'pointer', fontWeight:700, display:'flex', alignItems:'center', gap:4 }}>View All <FaChevronRight /></button>}
                />
                <div className="scroll-x">
                  <table className="mon-table">
                    <thead><tr>
                      <th>#</th><th>Name</th><th>Course</th><th>Submitted</th><th>Approved</th><th>Rate</th>
                    </tr></thead>
                    <tbody>
                      {students.slice(0,8).map((s,i) => (
                        <tr key={i}>
                          <td style={{ color:i<3?'#F39C12':'#aaa', fontWeight:i<3?700:400, fontSize:12 }}>{i+1}</td>
                          <td style={{ fontWeight:600 }}>{s.name||'—'}</td>
                          <td style={{ fontSize:12, color:'#777' }}>{s.course||'—'}</td>
                          <td><Badge label={s.totalSubmissions} color="#4A90E2" bg="#4A90E215" /></td>
                          <td><Badge label={s.approved} color="#27AE60" bg="#27AE6015" /></td>
                          <td><Badge label={`${s.approvalRate}%`} color='#fff' bg={rateColor(s.approvalRate)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ══════════════ DEPARTMENTS ══════════════ */}
        {tab === 'departments' && program === 'UG' && <UGDepartmentsTab api={api} token={token} />}
        {tab === 'departments' && program === 'PG' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <SearchInput value={deptSearch} onChange={setDeptSearch} placeholder="Search departments…" />
            </div>
            {filteredDepts.length === 0 && (
              <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No departments found.</p></Card>
            )}
            {filteredDepts.map((dept,i) => {
              const c = COLORS[i % COLORS.length];
              return (
                <Card key={dept.courseId} style={{ border:`1px solid ${c}30`, overflow:'hidden', padding:0 }}>
                  {/* Dept header */}
                  <div style={{ background:c+'12', borderLeft:`5px solid ${c}`, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                    <div>
                      <span style={{ fontWeight:800, fontSize:15, color:'#1a1a2e' }}>{dept.courseName}</span>
                      {dept.courseCode && <span style={{ marginLeft:8, fontSize:11, color:'#888', background:'#fff', padding:'2px 8px', borderRadius:20 }}>{dept.courseCode}</span>}
                    </div>
                    <div style={{ display:'flex', gap:10, fontSize:12, flexWrap:'wrap' }}>
                      <span><strong style={{ color:'#4A90E2', fontSize:16 }}>{dept.studentCount}</strong> <span style={{ color:'#888' }}>students</span></span>
                      <span><strong style={{ color:'#9B59B6', fontSize:16 }}>{dept.teacherCount}</strong> <span style={{ color:'#888' }}>teachers</span></span>
                      <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <strong style={{ color:rateColor(dept.approvalRate) }}>{dept.approvalRate}%</strong> <span style={{ color:'#888' }}>approval</span>
                      </span>
                    </div>
                  </div>

                  <div style={{ padding:'12px 16px' }}>
                    {/* Stats */}
                    <div style={{ display:'flex', gap:20, fontSize:12, color:'#555', flexWrap:'wrap', marginBottom:12 }}>
                      <span>📚 <strong>{dept.logbookCount}</strong> logbook entries</span>
                      <span>✅ <strong>{dept.approvedCount}</strong> approved</span>
                      {dept.hods.length > 0 && <span>👑 HOD: <strong>{dept.hods.join(', ')}</strong></span>}
                    </div>

                    {/* Approval progress */}
                    <div style={{ marginBottom:12 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#888', marginBottom:4 }}>
                        <span>Approval Progress</span>
                        <span style={{ fontWeight:700, color:rateColor(dept.approvalRate) }}>{dept.approvalRate}%</span>
                      </div>
                      <ProgressBar value={dept.approvalRate} color={rateColor(dept.approvalRate)} height={8} />
                    </div>

                    {/* Teachers */}
                    {dept.teachers.length > 0 && (
                      <div>
                        <div style={{ fontSize:11, color:'#888', fontWeight:700, marginBottom:6 }}>FACULTY</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                          {dept.teachers.map((t,ti) => (
                            <span
                              key={ti}
                              onClick={() => {
                                const found = teachers.find(tc => tc.name === t.name || tc.email === t.email);
                                if (found?._id) setSelectedTeacherId(found._id);
                              }}
                              style={{ fontSize:11, padding:'4px 10px', borderRadius:20, background: t.isHOD ? MAROON+'18' : '#f0f0f0', color: t.isHOD ? MAROON : '#555', border:`1px solid ${t.isHOD ? MAROON+'40' : '#e0e0e0'}`, fontWeight: t.isHOD ? 700 : 400, cursor:'pointer', transition:'opacity 0.15s' }}
                              title="View teacher analytics"
                            >
                              {t.isHOD ? '★ ' : ''}{t.name}{t.designation ? ` (${t.designation})` : ''}{leftDateLabel(t.leavingDate)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* ══════════════ STUDENTS ══════════════ */}
        {tab === 'students' && program === 'UG' && (
          <UGStudentsTab api={api} token={token} onSelectStudent={setSelectedStudentId} />
        )}
        {tab === 'students' && program === 'PG' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

            {/* ── Download Report card ── */}
            <Card style={{ border:`1px solid ${MAROON}25`, background:`${MAROON}06` }}>
              <SectionHeader icon={<FaDownload />} title="Download Student Activity Report" color={MAROON} />
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
                {/* Year */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Year</label>
                  <select value={rptYear} onChange={e => setRptYear(e.target.value)}
                    style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:110 }}>
                    <option value=''>All Years</option>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                {/* Month */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Month</label>
                  <select value={rptMonth} onChange={e => setRptMonth(e.target.value)}
                    style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:130 }}>
                    <option value=''>All Months</option>
                    {MONTHS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
                  </select>
                </div>
                {/* Batch */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Batch</label>
                  <select value={rptBatch} onChange={e => setRptBatch(e.target.value)}
                    style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:130 }}>
                    <option value=''>All Batches</option>
                    {batchOptions.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                {/* Course */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Course / Dept</label>
                  <select value={rptCourse} onChange={e => setRptCourse(e.target.value)}
                    style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:160 }}>
                    <option value=''>All Courses</option>
                    {departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
                  </select>
                </div>
                {/* Buttons */}
                <div style={{ display:'flex', gap:8, alignSelf:'flex-end' }}>
                  <button
                    onClick={handleViewStudentReport}
                    disabled={rptLoading}
                    style={{
                      display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                      background: rptLoading ? '#ccc' : '#4A90E2', color:'#fff',
                      border:'none', borderRadius:8, fontSize:13, fontWeight:700,
                      cursor: rptLoading ? 'not-allowed' : 'pointer',
                      whiteSpace:'nowrap',
                    }}
                  >
                    <FaChartBar />
                    {rptLoading ? 'Loading…' : 'View'}
                  </button>
                  <button
                    onClick={handleDownloadStudentReport}
                    disabled={rptLoading}
                    style={{
                      display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                      background: rptLoading ? '#ccc' : MAROON, color:'#fff',
                      border:'none', borderRadius:8, fontSize:13, fontWeight:700,
                      cursor: rptLoading ? 'not-allowed' : 'pointer',
                      whiteSpace:'nowrap',
                    }}
                  >
                    <FaDownload />
                    {rptLoading ? 'Generating…' : 'Download PDF'}
                  </button>
                </div>
              </div>

              {/* ── Inline report results ── */}
              {rptData && (
                <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:14 }}>
                  {/* Period label + summary */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:MAROON }}>Period: {rptData.periodLabel}</span>
                    <button onClick={() => setRptData(null)}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#aaa', fontSize:18, lineHeight:1 }}>
                      <FaTimes />
                    </button>
                  </div>

                  {/* Summary row */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10 }}>
                    <StatCard icon={<FaUserGraduate />} label="Active Students" value={rptData.activeStudents.length} color="#27AE60" />
                    <StatCard icon={<FaUsers />} label="Inactive Students" value={rptData.inactiveStudents.length} color="#E74C3C" />
                    <StatCard icon={<FaBookMedical />} label="Total Entries" value={rptData.activeStudents.reduce((s,r)=>s+r.entryCount,0)} color="#E67E22" />
                  </div>

                  {/* Active students table */}
                  <Card>
                    <SectionHeader icon={<FaCheckCircle />} title={`Active Students — ${rptData.activeStudents.length}`} color="#27AE60" />
                    {rptData.activeStudents.length === 0 ? (
                      <p style={{ color:'#999', textAlign:'center', padding:16, fontSize:13 }}>No active students for this period.</p>
                    ) : (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead>
                            <tr>
                              {['#','Name','Reg. No.','Course','Batch','Entries'].map(h => (
                                <th key={h} style={{ background:'#f0faf4', padding:'8px 10px', textAlign:(h==='Name'||h==='Course')?'left':'center', fontWeight:700, color:'#27AE60', borderBottom:'2px solid #c3e6cb', fontSize:11 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rptData.activeStudents.map((s,i) => (
                              <tr key={i} style={{ borderBottom:'1px solid #f0f0f0', background: i%2===0?'#fff':'#f9fef9' }}>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:i<3?'#F39C12':'#aaa', fontWeight:i<3?700:400 }}>{i+1}</td>
                                <td style={{ padding:'7px 10px', fontWeight:600 }}>{s.name}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#888', fontSize:11 }}>{s.registrationNumber}</td>
                                <td style={{ padding:'7px 10px', fontSize:11 }}>{s.course}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', fontSize:11 }}>{s.batch}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', fontWeight:800, color:'#27AE60', fontSize:14 }}>{s.entryCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>

                  {/* Inactive students table */}
                  <Card>
                    <SectionHeader icon={<FaClock />} title={`Inactive Students — ${rptData.inactiveStudents.length}`} color="#E74C3C" />
                    {rptData.inactiveStudents.length === 0 ? (
                      <p style={{ color:'#999', textAlign:'center', padding:16, fontSize:13 }}>All students were active in this period.</p>
                    ) : (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead>
                            <tr>
                              {['#','Name','Reg. No.','Course','Batch','Account Status'].map(h => (
                                <th key={h} style={{ background:'#fdf0f0', padding:'8px 10px', textAlign:(h==='Name'||h==='Course')?'left':'center', fontWeight:700, color:'#E74C3C', borderBottom:'2px solid #f5c6cb', fontSize:11 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rptData.inactiveStudents.map((s,i) => (
                              <tr key={i} style={{ borderBottom:'1px solid #f0f0f0', background: i%2===0?'#fff':'#fff8f8' }}>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#aaa' }}>{i+1}</td>
                                <td style={{ padding:'7px 10px', fontWeight:600 }}>{s.name}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#888', fontSize:11 }}>{s.registrationNumber}</td>
                                <td style={{ padding:'7px 10px', fontSize:11 }}>{s.course}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', fontSize:11 }}>{s.batch}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center' }}>
                                  <Badge label={s.isAccountActive ? 'Active' : 'Deactivated'} color='#fff' bg={s.isAccountActive ? '#27AE60' : '#E74C3C'} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </Card>

            {/* ── Bulk logbook download by batch ── */}
            <Card style={{ border:`1px solid #4A90E225`, background:`#4A90E206` }}>
              <SectionHeader icon={<FaDownload />} title="Download All Logbooks by Batch" color="#4A90E2" />
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Department</label>
                  <select value={bulkCourseId} onChange={e => setBulkCourseId(e.target.value)}
                    style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:180 }}>
                    <option value=''>All Departments</option>
                    {departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
                  </select>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Batch</label>
                  <select value={bulkBatch} onChange={e => setBulkBatch(e.target.value)}
                    style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:160 }}>
                    <option value=''>— Select Batch —</option>
                    {bulkBatchOptions.map(b => <option key={b} value={b}>{batchLabels[b] || b}</option>)}
                  </select>
                </div>
                <button
                  onClick={() => setBulkConfirmOpen(true)}
                  disabled={!bulkBatch || bulkMatchedStudents.length === 0 || bulkDownloading}
                  style={{
                    display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                    background: (!bulkBatch || bulkMatchedStudents.length === 0 || bulkDownloading) ? '#ccc' : '#4A90E2', color:'#fff',
                    border:'none', borderRadius:8, fontSize:13, fontWeight:700,
                    cursor: (!bulkBatch || bulkMatchedStudents.length === 0 || bulkDownloading) ? 'not-allowed' : 'pointer',
                    whiteSpace:'nowrap',
                  }}
                >
                  <FaDownload />
                  {bulkDownloading ? 'Downloading…' : 'Download All'}
                </button>
              </div>
              {bulkBatch && !bulkDownloading && (
                <p style={{ margin:'10px 0 0', fontSize:12, color:'#666' }}>
                  {bulkMatchedStudents.length === 0
                    ? 'No students found in this batch.'
                    : `${bulkMatchedStudents.length} student${bulkMatchedStudents.length !== 1 ? 's' : ''} in this batch will get a logbook PDF.`}
                </p>
              )}
              {bulkDownloading && bulkProgress && (
                <div style={{ marginTop:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#555', marginBottom:5 }}>
                    <span>Generating {bulkProgress.current} of {bulkProgress.total} — {bulkProgress.name}</span>
                    <span style={{ fontWeight:700, color:'#4A90E2' }}>{Math.round((bulkProgress.current / bulkProgress.total) * 100)}%</span>
                  </div>
                  <ProgressBar value={Math.round((bulkProgress.current / bulkProgress.total) * 100)} color="#4A90E2" height={8} />
                </div>
              )}
            </Card>

            {/* Filters */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <SearchInput value={studentSearch} onChange={setStudentSearch} placeholder="Search by name, reg. no. or email…" />
              <select
                value={studentCourseFilter}
                onChange={e => setStudentCourseFilter(e.target.value)}
                style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:160 }}
              >
                <option value=''>All Courses</option>
                {departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
              </select>
              <select
                value={studentBatchFilter}
                onChange={e => setStudentBatchFilter(e.target.value)}
                style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:140 }}
              >
                <option value=''>All Batches</option>
                {batchOptions.map(b => <option key={b} value={b}>{batchLabels[b] || b}</option>)}
              </select>
            </div>

            {allStudentsLoading ? (
              <div style={{ display:'flex', justifyContent:'center', padding:50 }}><Spinner /></div>
            ) : (
              <>
                <div style={{ fontSize:13, color:'#777', fontWeight:600 }}>
                  Showing {filteredStudents.length === 0 ? 0 : (studentsPage - 1) * STUDENTS_PER_PAGE + 1}–{Math.min(studentsPage * STUDENTS_PER_PAGE, filteredStudents.length)} of {filteredStudents.length} students
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {filteredStudents.length === 0 && (
                    <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No students found.</p></Card>
                  )}
                  {pagedStudents.map(s => {
                    const rc = rateColor(s.approvalRate);
                    const studentBatches = [...new Set((s.courses || []).map(c => c.batch).filter(Boolean))];
                    return (
                      <Card
                        key={s._id}
                        hoverable
                        onClick={() => setSelectedStudentId(s._id)}
                        style={{ padding:'14px 16px', cursor:'pointer' }}
                      >
                        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                          <div style={{ width:42, height:42, borderRadius:'50%', background:MAROON+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:MAROON, fontWeight:700, flexShrink:0 }}>
                            {(s.name||'?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                              <span style={{ fontWeight:700, fontSize:14, color:'#1a1a2e' }}>{s.name}</span>
                              {s.registrationNumber && <span style={{ fontSize:11, color:'#888', background:'#f0f0f0', padding:'2px 7px', borderRadius:20 }}>{s.registrationNumber}</span>}
                              {studentBatches.map(b => (
                                <span key={b} style={{ fontSize:11, color:MAROON, background:MAROON+'15', padding:'2px 7px', borderRadius:20, fontWeight:700 }}>{batchLabels[b] || b}</span>
                              ))}
                              {!s.isActive && <Badge label="Inactive" color='#fff' bg='#E74C3C' />}
                            </div>
                            <div style={{ fontSize:11, color:'#888', marginTop:3 }}>
                              {s.courses.map(c => c.courseName).filter(Boolean).join(' · ') || s.email}
                            </div>
                            {!teacherMapLoading && (() => {
                              const faculty = [...new Set((studentFacultyMap[s._id?.toString()] || []).map(f => f.teacherName))];
                              return faculty.length > 0 ? (
                                <div style={{ fontSize:11, color:'#9B59B6', marginTop:3, fontWeight:600 }}>
                                  Faculty: {faculty.join(', ')}
                                </div>
                              ) : (
                                <div style={{ fontSize:11, color:'#bbb', marginTop:3, fontStyle:'italic' }}>
                                  No faculty assigned
                                </div>
                              );
                            })()}
                          </div>
                          <div style={{ display:'flex', gap:14, alignItems:'center', flexShrink:0 }}>
                            <div style={{ textAlign:'center' }}>
                              <div style={{ fontSize:18, fontWeight:800, color:'#4A90E2' }}>{s.totalLogbook}</div>
                              <div style={{ fontSize:10, color:'#aaa' }}>Entries</div>
                            </div>
                            <div style={{ textAlign:'center' }}>
                              <div style={{ fontSize:18, fontWeight:800, color:rc }}>{s.approvalRate}%</div>
                              <div style={{ fontSize:10, color:'#aaa' }}>Approved</div>
                            </div>
                            {s.drpStatus !== null && (
                              <div style={{ textAlign:'center' }}>
                                <Badge
                                  label={s.drpStatus?.certified ? '✓ DRP' : s.drpStatus?.hasProfile ? 'DRP Active' : 'No DRP'}
                                  color='#fff'
                                  bg={s.drpStatus?.certified ? MAROON : s.drpStatus?.hasProfile ? '#27AE60' : '#aaa'}
                                />
                              </div>
                            )}
                            <button
                              onClick={e => { e.stopPropagation(); setDownloadStudent(s); }}
                              disabled={downloadingStudentId === s._id}
                              title="Download Logbook PDF"
                              style={{ background:'#4A90E215', border:'none', borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', color:'#4A90E2', cursor: downloadingStudentId === s._id ? 'default' : 'pointer', flexShrink:0 }}
                            >
                              {downloadingStudentId === s._id ? <Spinner size={14} /> : <FaDownload size={13} />}
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); setSelectedStudentId(s._id); }}
                              title="View Report"
                              style={{ background:MAROON+'15', border:'none', borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', color:MAROON, cursor:'pointer', flexShrink:0 }}
                            >
                              <FaFileAlt size={13} />
                            </button>
                            <FaChevronRight style={{ color:'#ccc', fontSize:13 }} />
                          </div>
                        </div>
                        <div style={{ marginTop:10 }}>
                          <ProgressBar value={s.approvalRate} color={rc} height={5} />
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {studentsTotalPages > 1 && (
                  <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:6 }}>
                    <button
                      onClick={() => setStudentsPage(p => Math.max(1, p - 1))}
                      disabled={studentsPage === 1}
                      style={{ padding:'7px 12px', border:'1px solid #e5e7eb', background:'#fff', borderRadius:8, fontSize:12, fontWeight:600, color: studentsPage === 1 ? '#ccc' : '#555', cursor: studentsPage === 1 ? 'default' : 'pointer' }}
                    >
                      Prev
                    </button>
                    {Array.from({ length: studentsTotalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === studentsTotalPages || Math.abs(p - studentsPage) <= 2)
                      .map((p, i, arr) => (
                        <React.Fragment key={p}>
                          {i > 0 && arr[i - 1] !== p - 1 && <span style={{ color:'#ccc', fontSize:12 }}>…</span>}
                          <button
                            onClick={() => setStudentsPage(p)}
                            style={{
                              width:32, height:32, border:'1px solid ' + (p === studentsPage ? MAROON : '#e5e7eb'),
                              background: p === studentsPage ? MAROON : '#fff',
                              color: p === studentsPage ? '#fff' : '#555',
                              borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer'
                            }}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      ))}
                    <button
                      onClick={() => setStudentsPage(p => Math.min(studentsTotalPages, p + 1))}
                      disabled={studentsPage === studentsTotalPages}
                      style={{ padding:'7px 12px', border:'1px solid #e5e7eb', background:'#fff', borderRadius:8, fontSize:12, fontWeight:600, color: studentsPage === studentsTotalPages ? '#ccc' : '#555', cursor: studentsPage === studentsTotalPages ? 'default' : 'pointer' }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════════ TEACHERS ══════════════ */}
        {tab === 'teachers' && program === 'UG' && (
          <UGTeachersTab api={api} token={token} onSelectTeacher={setSelectedTeacherId} />
        )}
        {tab === 'teachers' && program === 'PG' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

            {/* ── Download Report card ── */}
            <Card style={{ border:`1px solid ${MAROON}25`, background:`${MAROON}06` }}>
              <SectionHeader icon={<FaDownload />} title="Download Faculty Activity Report" color={MAROON} />
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
                {/* Year */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Year</label>
                  <select value={rptTeacherYear} onChange={e => setRptTeacherYear(e.target.value)}
                    style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:110 }}>
                    <option value=''>All Years</option>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                {/* Month */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Month</label>
                  <select value={rptTeacherMonth} onChange={e => setRptTeacherMonth(e.target.value)}
                    style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:130 }}>
                    <option value=''>All Months</option>
                    {MONTHS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
                  </select>
                </div>
                {/* Course */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Course / Dept</label>
                  <select value={rptTeacherCourse} onChange={e => setRptTeacherCourse(e.target.value)}
                    style={{ padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, background:'#fff', minWidth:160 }}>
                    <option value=''>All Courses</option>
                    {departments.map(d => <option key={d.courseId} value={d.courseId}>{d.courseName}</option>)}
                  </select>
                </div>
                {/* Buttons */}
                <div style={{ display:'flex', gap:8, alignSelf:'flex-end' }}>
                  <button
                    onClick={handleViewTeacherReport}
                    disabled={rptTeacherLoading}
                    style={{
                      display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                      background: rptTeacherLoading ? '#ccc' : '#4A90E2', color:'#fff',
                      border:'none', borderRadius:8, fontSize:13, fontWeight:700,
                      cursor: rptTeacherLoading ? 'not-allowed' : 'pointer',
                      whiteSpace:'nowrap',
                    }}
                  >
                    <FaChartBar />
                    {rptTeacherLoading ? 'Loading…' : 'View'}
                  </button>
                  <button
                    onClick={handleDownloadTeacherReport}
                    disabled={rptTeacherLoading}
                    style={{
                      display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                      background: rptTeacherLoading ? '#ccc' : MAROON, color:'#fff',
                      border:'none', borderRadius:8, fontSize:13, fontWeight:700,
                      cursor: rptTeacherLoading ? 'not-allowed' : 'pointer',
                      whiteSpace:'nowrap',
                    }}
                  >
                    <FaDownload />
                    {rptTeacherLoading ? 'Generating…' : 'Download PDF'}
                  </button>
                </div>
              </div>

              {/* ── Inline report results ── */}
              {rptTeacherData && (
                <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:14 }}>
                  {/* Period label + summary */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:MAROON }}>Period: {rptTeacherData.periodLabel}</span>
                    <button onClick={() => setRptTeacherData(null)}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#aaa', fontSize:18, lineHeight:1 }}>
                      <FaTimes />
                    </button>
                  </div>

                  {/* Summary row */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10 }}>
                    <StatCard icon={<FaChalkboardTeacher />} label="Active Faculty" value={rptTeacherData.activeTeachers.length} color="#27AE60" />
                    <StatCard icon={<FaUsers />} label="Inactive Faculty" value={rptTeacherData.inactiveTeachers.length} color="#E74C3C" />
                    <StatCard icon={<FaCheckCircle />} label="Total Reviews" value={rptTeacherData.activeTeachers.reduce((s,r)=>s+r.reviewed,0)} color="#4A90E2" />
                  </div>

                  {/* Active faculty table */}
                  <Card>
                    <SectionHeader icon={<FaCheckCircle />} title={`Active Faculty — ${rptTeacherData.activeTeachers.length}`} color="#27AE60" />
                    {rptTeacherData.activeTeachers.length === 0 ? (
                      <p style={{ color:'#999', textAlign:'center', padding:16, fontSize:13 }}>No active faculty for this period.</p>
                    ) : (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead>
                            <tr>
                              {['#','Name','Courses','Reviewed','Approved','Rejected','Needs Rev.','Rate','Avg Days'].map(h => (
                                <th key={h} style={{ background:'#f0faf4', padding:'8px 10px', textAlign:(h==='Name'||h==='Courses')?'left':'center', fontWeight:700, color:'#27AE60', borderBottom:'2px solid #c3e6cb', fontSize:11 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rptTeacherData.activeTeachers.map((t,i) => (
                              <tr key={i} style={{ borderBottom:'1px solid #f0f0f0', background: i%2===0?'#fff':'#f9fef9' }}>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:i<3?'#F39C12':'#aaa', fontWeight:i<3?700:400 }}>{i+1}</td>
                                <td style={{ padding:'7px 10px', fontWeight:600 }}>{t.name}{t.designation ? ` (${t.designation})` : ''}{t.isHOD ? ' (HOD)' : ''}{leftDateLabel(t.leavingDate)}</td>
                                <td style={{ padding:'7px 10px', fontSize:11 }}>{t.courses.join(', ') || '—'}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', fontWeight:800, color:'#27AE60', fontSize:14 }}>{t.reviewed}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#27AE60' }}>{t.approved}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#E74C3C' }}>{t.rejected}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#F39C12' }}>{t.needsRevision}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center' }}><Badge label={`${t.approvalRate}%`} color='#fff' bg={rateColor(t.approvalRate)} /></td>
                                <td style={{ padding:'7px 10px', textAlign:'center', fontSize:11 }}>{t.avgTurnaroundDays != null ? t.avgTurnaroundDays : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>

                  {/* Inactive faculty table */}
                  <Card>
                    <SectionHeader icon={<FaClock />} title={`Inactive Faculty — ${rptTeacherData.inactiveTeachers.length}`} color="#E74C3C" />
                    {rptTeacherData.inactiveTeachers.length === 0 ? (
                      <p style={{ color:'#999', textAlign:'center', padding:16, fontSize:13 }}>All faculty were active in this period.</p>
                    ) : (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead>
                            <tr>
                              {['#','Name','Email','Courses','Account Status'].map(h => (
                                <th key={h} style={{ background:'#fdf0f0', padding:'8px 10px', textAlign:(h==='Name'||h==='Courses'||h==='Email')?'left':'center', fontWeight:700, color:'#E74C3C', borderBottom:'2px solid #f5c6cb', fontSize:11 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rptTeacherData.inactiveTeachers.map((t,i) => (
                              <tr key={i} style={{ borderBottom:'1px solid #f0f0f0', background: i%2===0?'#fff':'#fff8f8' }}>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#aaa' }}>{i+1}</td>
                                <td style={{ padding:'7px 10px', fontWeight:600 }}>{t.name}{t.designation ? ` (${t.designation})` : ''}{t.isHOD ? ' (HOD)' : ''}{leftDateLabel(t.leavingDate)}</td>
                                <td style={{ padding:'7px 10px', fontSize:11, color:'#888' }}>{t.email}</td>
                                <td style={{ padding:'7px 10px', fontSize:11 }}>{t.courses.join(', ') || '—'}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center' }}>
                                  <Badge label={t.isAccountActive ? 'Active' : 'Deactivated'} color='#fff' bg={t.isAccountActive ? '#27AE60' : '#E74C3C'} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </Card>

            {/* Top chart */}
            {filteredTeachers.length > 0 && (
              <Card>
                <SectionHeader icon={<FaChartBar />} title="Top Faculty by Reviews Completed" color="#9B59B6" />
                <ResponsiveContainer width="100%" height={Math.min(filteredTeachers.length, 10) * 26 + 10}>
                  <BarChart data={filteredTeachers.slice(0,10).map(t => ({ name:chartFacultyLabel(t.name), reviewed:t.reviewed, pending:t.pending }))} layout="vertical" margin={{ top:0, right:24, left:20, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize:11 }} />
                    <YAxis dataKey="name" type="category" width={110} tick={{ fontSize:11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize:12 }} />
                    <Bar dataKey="reviewed" name="Reviewed" fill="#27AE60" radius={[0,4,4,0]} />
                    <Bar dataKey="pending" name="Pending" fill="#F39C12" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <SearchInput value={teacherSearch} onChange={setTeacherSearch} placeholder="Search faculty…" />
              <select
                value={teacherDeptFilter}
                onChange={e => setTeacherDeptFilter(e.target.value)}
                style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:160 }}
              >
                <option value=''>All Departments</option>
                {teacherDeptOptions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select
                value={teacherDesignationFilter}
                onChange={e => setTeacherDesignationFilter(e.target.value)}
                style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa', minWidth:160 }}
              >
                <option value=''>All Designations</option>
                {teacherDesignationOptions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div style={{ fontSize:13, color:'#777', fontWeight:600 }}>
              Showing {filteredTeachers.length === 0 ? 0 : (teachersPage - 1) * TEACHERS_PER_PAGE + 1}–{Math.min(teachersPage * TEACHERS_PER_PAGE, filteredTeachers.length)} of {filteredTeachers.length} faculty
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {filteredTeachers.length === 0 && <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No faculty found.</p></Card>}
              {pagedTeachers.map(t => (
                <Card key={t._id || t.email} hoverable onClick={() => t._id && setSelectedTeacherId(t._id)} style={{ padding:'14px 16px', cursor:'pointer' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <div style={{ width:42, height:42, borderRadius:'50%', background:'#9B59B615', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:'#9B59B6', fontWeight:700, flexShrink:0 }}>
                      {(t.name||'?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <span style={{ fontWeight:700, fontSize:14, color:'#1a1a2e' }}>{t.name||'—'}{leftDateLabel(t.leavingDate)}</span>
                        {t.isHOD && <Badge label="HOD" color='#fff' bg={MAROON} />}
                        {t.designation && <Badge label={t.designation} color='#2980b9' bg="#eaf4fb" />}
                      </div>
                      <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{t.email}</div>
                      <div style={{ fontSize:11, color:'#777', marginTop:2 }}>{t.courses.join(', ')||'—'}</div>
                      {!teacherMapLoading && (
                        <button
                          onClick={e => { e.stopPropagation(); if (t._id) setViewStudentsTeacherId(t._id); }}
                          style={{
                            marginTop:8, display:'inline-flex', alignItems:'center', gap:6,
                            padding:'5px 12px', border:`1px solid ${MAROON}30`, background:MAROON+'0c',
                            color:MAROON, borderRadius:20, fontSize:11.5, fontWeight:700, cursor:'pointer'
                          }}
                        >
                          <FaUsers /> View Students Assigned ({teacherStudentsMap[t._id?.toString()]?.studentIds.size || 0})
                        </button>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:14, alignItems:'center', flexShrink:0 }}>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:18, fontWeight:800, color:'#27AE60' }}>{t.reviewed}</div>
                        <div style={{ fontSize:10, color:'#aaa' }}>Reviewed</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:18, fontWeight:800, color: t.pending>0?'#F39C12':'#ccc' }}>{t.pending}</div>
                        <div style={{ fontSize:10, color:'#aaa' }}>Pending</div>
                      </div>
                      <FaChevronRight style={{ color:'#ccc', fontSize:13 }} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {teachersTotalPages > 1 && (
              <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:6 }}>
                <button
                  onClick={() => setTeachersPage(p => Math.max(1, p - 1))}
                  disabled={teachersPage === 1}
                  style={{ padding:'7px 12px', border:'1px solid #e5e7eb', background:'#fff', borderRadius:8, fontSize:12, fontWeight:600, color: teachersPage === 1 ? '#ccc' : '#555', cursor: teachersPage === 1 ? 'default' : 'pointer' }}
                >
                  Prev
                </button>
                {Array.from({ length: teachersTotalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === teachersTotalPages || Math.abs(p - teachersPage) <= 2)
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && <span style={{ color:'#ccc', fontSize:12 }}>…</span>}
                      <button
                        onClick={() => setTeachersPage(p)}
                        style={{
                          width:32, height:32, border:'1px solid ' + (p === teachersPage ? MAROON : '#e5e7eb'),
                          background: p === teachersPage ? MAROON : '#fff',
                          color: p === teachersPage ? '#fff' : '#555',
                          borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer'
                        }}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button
                  onClick={() => setTeachersPage(p => Math.min(teachersTotalPages, p + 1))}
                  disabled={teachersPage === teachersTotalPages}
                  style={{ padding:'7px 12px', border:'1px solid #e5e7eb', background:'#fff', borderRadius:8, fontSize:12, fontWeight:600, color: teachersPage === teachersTotalPages ? '#ccc' : '#555', cursor: teachersPage === teachersTotalPages ? 'default' : 'pointer' }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══════════════ FACULTY ↔ STUDENT MAP ══════════════ */}
        {tab === 'team' && program === 'UG' && (
          <UGTeamTab api={api} token={token} />
        )}
        {tab === 'team' && program === 'PG' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <SearchInput value={teamSearch} onChange={setTeamSearch} placeholder="Search by faculty, department, or student…" />
            </div>

            {teacherMapLoading ? (
              <div style={{ display:'flex', justifyContent:'center', padding:50 }}><Spinner /></div>
            ) : teacherMapByDept.length === 0 ? (
              <Card><p style={{ color:'#999', textAlign:'center', padding:20 }}>No faculty assigned to any course found.</p></Card>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                {teacherMapByDept.map((dept, di) => {
                  const c = COLORS[di % COLORS.length];
                  const totalStudents = new Set(dept.rows.flatMap(r => r.students.map(s => s._id?.toString()))).size;
                  return (
                    <Card key={dept.courseId || dept.courseName} style={{ border:`1px solid ${c}30`, overflow:'hidden', padding:0 }}>
                      {/* Department header */}
                      <div style={{ background:c+'12', borderLeft:`5px solid ${c}`, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <FaUniversity style={{ color:c, fontSize:15 }} />
                          <span style={{ fontWeight:800, fontSize:15, color:'#1a1a2e' }}>{dept.courseName}</span>
                        </div>
                        <div style={{ display:'flex', gap:10, fontSize:12, flexWrap:'wrap' }}>
                          <span><strong style={{ color:'#9B59B6', fontSize:16 }}>{dept.rows.length}</strong> <span style={{ color:'#888' }}>faculty</span></span>
                          <span><strong style={{ color:'#4A90E2', fontSize:16 }}>{totalStudents}</strong> <span style={{ color:'#888' }}>students assigned</span></span>
                        </div>
                      </div>

                      {/* Faculty rows within this department */}
                      <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>
                        {dept.rows.map((row, i) => (
                          <div key={i} style={{ background:'#f8f9fa', borderRadius:10, padding:'12px 14px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                              <div
                                onClick={() => row.teacherId && setSelectedTeacherId(row.teacherId)}
                                style={{ width:38, height:38, borderRadius:'50%', background:'#9B59B615', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, color:'#9B59B6', fontWeight:700, flexShrink:0, cursor: row.teacherId ? 'pointer' : 'default' }}
                              >
                                {(row.teacherName||'?')[0].toUpperCase()}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                  <span
                                    onClick={() => row.teacherId && setSelectedTeacherId(row.teacherId)}
                                    style={{ fontWeight:700, fontSize:13.5, color:'#1a1a2e', cursor: row.teacherId ? 'pointer' : 'default' }}
                                  >
                                    {row.teacherName}
                                  </span>
                                  <Badge label="Faculty" color="#9B59B6" bg="#9B59B615" />
                                  {!row.teacherActive && <Badge label="Inactive" color='#fff' bg='#E74C3C' />}
                                </div>
                                <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{row.teacherEmail}</div>
                              </div>
                              <div style={{ textAlign:'center', flexShrink:0 }}>
                                <div style={{ fontSize:17, fontWeight:800, color:'#4A90E2' }}>{row.students.length}</div>
                                <div style={{ fontSize:10, color:'#aaa' }}>Students</div>
                              </div>
                            </div>
                            {row.students.length > 0 && (() => {
                              const batchCounts = row.students.reduce((acc, s) => {
                                const b = s.batch || 'Unassigned';
                                acc[b] = (acc[b] || 0) + 1;
                                return acc;
                              }, {});
                              const batchEntries = Object.entries(batchCounts).sort((a, b) =>
                                a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' })
                              );
                              return (
                                <>
                                  {batchEntries.length > 1 && (
                                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:10 }}>
                                      {batchEntries.map(([b, count]) => (
                                        <span key={b} style={{ fontSize:10.5, padding:'3px 9px', borderRadius:20, background:MAROON+'12', color:MAROON, border:`1px solid ${MAROON}30`, fontWeight:700 }}>
                                          {batchLabels[b] || b}: {count}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:8 }}>
                                    {row.students.map((s, si) => (
                                      <span key={si} style={{ fontSize:11, padding:'4px 10px', borderRadius:20, background:'#fff', color:'#555', border:'1px solid #e0e0e0' }}>
                                        {s.name}{s.registrationNumber ? ` (${s.registrationNumber})` : ''}{s.batch ? ` · ${batchLabels[s.batch] || s.batch}` : ''}
                                      </span>
                                    ))}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════ DRP ANALYTICS ══════════════ */}
        {tab === 'drp' && (
          <DRPStudentsTab api={api} token={token} departments={departments} />
        )}

        {/* ══════════════ EXAM ELIGIBILITY ══════════════ */}
        {tab === 'eligibility' && (
          <ExamEligibilityTab api={api} token={token} departments={departments} />
        )}

        {/* ══════════════ ACADEMIC MONITORING ══════════════ */}
        {tab === 'academic' && (
          <AcademicMonitoringTab api={api} token={token} departments={departments} program={program} />
        )}

        {/* ══════════════ STUDENT GRADES ══════════════ */}
        {tab === 'grades' && (
          <GradesTab api={api} token={token} departments={departments} program={program} />
        )}

        {/* ══════════════ REPORTS ══════════════ */}
        {tab === 'reports' && (
          <ReportsTab api={api} token={token} departments={departments} program={program} />
        )}

      </div>

      {/* ── Modals ── */}
      {selectedStudentId && program === 'PG' && (
        <StudentDetailModal
          studentId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
          token={token}
          api={api}
        />
      )}
      {selectedStudentId && program === 'UG' && (
        <UGStudentDetailModal
          studentId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
          token={token}
          api={api}
        />
      )}
      {selectedTeacherId && program === 'PG' && (
        <TeacherDetailModal
          teacherId={selectedTeacherId}
          onClose={() => setSelectedTeacherId(null)}
          token={token}
          api={api}
        />
      )}
      {selectedTeacherId && program === 'UG' && (
        <UGTeacherDetailModal
          teacherId={selectedTeacherId}
          onClose={() => setSelectedTeacherId(null)}
          token={token}
          api={api}
        />
      )}
      {viewStudentsTeacherId && (() => {
        const teacherEntry = teachers.find(t => t._id?.toString() === viewStudentsTeacherId.toString());
        const assigned = (teacherStudentsMap[viewStudentsTeacherId.toString()]?.students || [])
          .slice()
          .sort((a, b) =>
            (a.batch || '').localeCompare(b.batch || '', undefined, { numeric: true, sensitivity: 'base' }) ||
            a.name.localeCompare(b.name)
          );
        return (
          <Modal onClose={() => setViewStudentsTeacherId(null)} title={`Students Assigned — ${teacherEntry?.name || 'Faculty'}`}>
            <p style={{ margin:'0 0 12px', fontSize:12.5, color:'#888' }}>
              {assigned.length} student{assigned.length !== 1 ? 's' : ''} assigned
            </p>
            {assigned.length === 0 ? (
              <p style={{ color:'#999', textAlign:'center', padding:20 }}>No students assigned yet.</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:400, overflowY:'auto' }}>
                {assigned.map((s, i) => (
                  <div key={s._id || i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#f8f9fa', borderRadius:8 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:MAROON+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:MAROON, fontWeight:700, flexShrink:0 }}>
                      {(s.name||'?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#1a1a2e' }}>{s.name}</div>
                      <div style={{ fontSize:11, color:'#888' }}>
                        {s.registrationNumber || '—'}{s.courseName ? ` · ${s.courseName}` : ''}
                      </div>
                    </div>
                    {s.batch && (
                      <span style={{ fontSize:11, color:MAROON, background:MAROON+'15', padding:'3px 9px', borderRadius:20, fontWeight:700, flexShrink:0 }}>
                        {batchLabels[s.batch] || s.batch}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Modal>
        );
      })()}
      {downloadStudent && (
        <Modal onClose={() => setDownloadStudent(null)} title="Download Logbook PDF">
          <p style={{ margin:'0 0 16px', fontSize:13, color:'#666' }}>Choose whether to include the submission date column for <strong>{downloadStudent.name}</strong>.</p>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <button
              onClick={() => handleDownloadStudentLogbook(downloadStudent, true)}
              style={{ padding:'12px 16px', border:`1px solid ${MAROON}30`, background:MAROON+'10', color:MAROON, borderRadius:10, fontWeight:700, fontSize:13, cursor:'pointer' }}
            >
              Download with submission date
            </button>
            <button
              onClick={() => handleDownloadStudentLogbook(downloadStudent, false)}
              style={{ padding:'12px 16px', border:'1px solid #e5e7eb', background:'#f8f9fa', color:'#333', borderRadius:10, fontWeight:700, fontSize:13, cursor:'pointer' }}
            >
              Download without submission date
            </button>
          </div>
        </Modal>
      )}
      {bulkConfirmOpen && (
        <Modal onClose={() => setBulkConfirmOpen(false)} title="Download All Logbooks">
          <p style={{ margin:'0 0 16px', fontSize:13, color:'#666' }}>
            This will generate and download <strong>{bulkMatchedStudents.length}</strong> separate logbook PDF{bulkMatchedStudents.length !== 1 ? 's' : ''} for batch <strong>{batchLabels[bulkBatch] || bulkBatch}</strong>, one after another. Choose whether to include the submission date column.
            {bulkMatchedStudents.length > 5 && (
              <><br /><br />Your browser may ask permission to allow multiple downloads from this site — please allow it so every file saves.</>
            )}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <button
              onClick={() => handleBulkDownloadBatch(true)}
              style={{ padding:'12px 16px', border:'1px solid #4A90E230', background:'#4A90E210', color:'#4A90E2', borderRadius:10, fontWeight:700, fontSize:13, cursor:'pointer' }}
            >
              Download with submission date
            </button>
            <button
              onClick={() => handleBulkDownloadBatch(false)}
              style={{ padding:'12px 16px', border:'1px solid #e5e7eb', background:'#f8f9fa', color:'#333', borderRadius:10, fontWeight:700, fontSize:13, cursor:'pointer' }}
            >
              Download without submission date
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
